import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom';
import { RequireAuth } from '@/shared/auth/RequireAuth';
import { RequireDemoSystem } from '@/shared/demo/RequireDemoSystem';
import { RequireRfSimulator } from '@/shared/demo/RequireRfSimulator';
import { NewScreensSwap } from '@/app/newscreens/NewScreensSwap';
import { v2Routes } from '@/app/v2';
import { useAuthStore } from '@/shared/auth/store';
import { APP_SCREEN_ACCESS, isTestScreensAdmin } from '@/app/nav';
import { RequireWindow } from '@/app/RequireWindow';

/** slice-2849: the full testers hub is admin-only (chris/link/link1); other testers are
 *  redirected to the guided Test Screens queue. */
function TestScreensAdminGate({ children }: { children: ReactNode }) {
  const session = useAuthStore((s) => s.session);
  if (!isTestScreensAdmin(session?.userId)) {
    return <Navigate to="/test-screens" replace />;
  }
  return <>{children}</>;
}

/**
 * slice-3647 — RETIRED ROUTE: `/inventory/adjust` (the lock-less "Quick-Adjust" page).
 *
 * <p>RUSS on demo 2026-09-14, drilling Order Lines → Item Status → Adjust Inventory →
 * a content: "it just allows him to adjust, no lock or anything". Chris: "yes retire
 * quick-adjust and repoint". PB has ONE Adjust Inventory window, `w_contents_maint`, and
 * it opens with Change/Insert/Delete/Move disabled until the Unlock/Lock Location key
 * takes a real `fsp_lockit` lock (w_contents_maint.srw:1896-1903 → :1270-1290, ue_lock
 * :2163-2181 → :1308). PH10 had a second, lock-less one; it is deleted.
 *
 * <p>Every inbound drill now names `/inventory/contents-maint` DIRECTLY (pinned by
 * quickAdjustRouteRetirement.test.tsx) — this redirect exists only so the links testers
 * already filed (issue rows, retest URLs, bookmarks) do not 404, per dev-rules' route-
 * retirement rule. `:contentId` becomes contents-maint's `?contentId=` content seed (PB
 * w_contents_maint(content), il_show=1, is_filter "content_id = '…'", .srw:223-231) and
 * `:warehouseId` its cross-warehouse `?warehouseId=` override (ADR-0026: the BE validates
 * it against the accessible set). The bare path's legacy `?owner=&item=` tree-picker
 * context maps to the `?itemId=` seed; `owner` is dropped because the contents search has
 * no owner predicate — the session's own owner scope applies (ADR-0026).
 */
function RetiredQuickAdjustRedirect() {
  const { warehouseId, contentId } = useParams<{
    warehouseId?: string;
    contentId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const params = new URLSearchParams();
  const wh = (warehouseId ?? '').trim();
  const content = (contentId ?? '').trim();
  const item = (searchParams.get('item') ?? '').trim();
  if (wh) params.set('warehouseId', wh);
  if (content) params.set('contentId', content);
  if (!content && item) params.set('itemId', item);
  const qs = params.toString();
  return <Navigate to={`/inventory/contents-maint${qs ? `?${qs}` : ''}`} replace />;
}

// Feature pages are lazy-loaded. Heavier features (mqtt, charting) also need
// an explicit `manualChunks` entry in vite.config.ts — React.lazy alone doesn't
// always trigger a chunk split (rollup may inline small modules). See dev-rules.md §4.5.
const PingPage = lazy(() => import('@/features/system/ping/PingPage'));
// slice-3454: PB Help > About (w_phfc_about) — product/version/build panel.
const AboutPage = lazy(() => import('@/features/system/about/AboutPage'));
// slice-3549 (CHRIS): index of the hidden v2 experiment pages — see app/v2/README.md.
const V2ScreensPage = lazy(() => import('@/features/v2screens/V2ScreensPage'));
const LoginPage = lazy(() => import('@/features/auth/login/LoginPage'));
const HomePage = lazy(() => import('@/features/landing/HomePage'));
// Decision #19 (Chris 2026-07-05): w_wave_fastpick_pallbuild full port.
const WaveFastpickPallbuildPage = lazy(
  () => import('@/features/orders/wave-fastpick-pallbuild/WaveFastpickPallbuildPage'),
);
const OrderMaintenancePage = lazy(
  () => import('@/features/orders/maintenance/OrderMaintenancePage'),
);
const OrderInquiryPage = lazy(() => import('@/features/orders/inquiry/OrderInquiryPage'));
const GenReceiptPage = lazy(
  () => import('@/features/orders/genreceipt/GenReceiptPage'),
);
const OrderSplitPage = lazy(
  () => import('@/features/orders/split/OrderSplitPage'),
);
const GenRmaPage = lazy(
  () => import('@/features/orders/genrma/GenRmaPage'),
);
const ReceiptMaintenancePage = lazy(
  () => import('@/features/receipts/maintenance/ReceiptMaintenancePage'),
);
const ItemInquiryPage = lazy(() => import('@/features/items/inquiry/ItemInquiryPage'));
const ItemStatusPage = lazy(() => import('@/features/items/status/ItemStatusPage'));
// EXPERIMENT (Chris-only, 2026-08): redesigned Item Status. Deliberately NOT in nav.ts and
// NOT in the testers-hub catalog (scripts/generate-page-catalog.mjs walks nav.generated.json
// + nav.ts WINDOW_TO_ROUTE/m_* + EXTRA_ROUTES — never router.tsx — so a router-only route
// stays out of the catalog, the Test Screens queue, and hub badges).
const ItemStatus2Page = lazy(() => import('@/features/items/status2/ItemStatus2Page'));
const ItemMasterPage = lazy(() => import('@/features/items/master/ItemMasterPage'));
const ItemHoldPage = lazy(() => import('@/features/items/hold/ItemHoldPage'));
const CubiscanPage = lazy(() => import('@/features/items/cubiscan/CubiscanPage'));
const EphsJspsPage = lazy(() => import('@/features/config/ephsjsps/EphsJspsPage'));
const InterfaceServicesPage = lazy(
  () => import('@/features/interfaces/services/InterfaceServicesPage'),
);
const MoverOrderRequestPage = lazy(
  () => import('@/features/movers/orderrequest/MoverOrderRequestPage'),
);
const SeasonAssignPage = lazy(
  () => import('@/features/warehouse/seasonassign/SeasonAssignPage'),
);
const WaveTypeMaintPage = lazy(() => import('@/features/wavetype/maint/WaveTypeMaintPage'));
const SizeTypeMaintPage = lazy(() => import('@/features/items/sizetype/SizeTypeMaintPage'));
const ItemConfigProfilePage = lazy(
  () => import('@/features/items/configprofile/ItemConfigProfilePage'),
);
const AppointmentBoardPage = lazy(() => import('@/shared/appointments/AppointmentBoardPage'));
// slice-1077: parallel-agent workstation rebuilds — dedicated pages that shadow
// the generic /browse/:key route for these screens.
const WorkAdjustmentPage = lazy(() => import('@/features/work/adjustment/WorkAdjustmentPage'));
const DockManagerPage = lazy(() => import('@/features/truck/dockmanager/DockManagerPage'));
const ReportLauncherPage = lazy(() => import('@/features/reports/launcher/ReportLauncherPage'));
// W1+W2 decision (2026-07-05): one productivity workbench replaces 6 PB report menu items.
const ProductivityWorkbenchPage = lazy(
  () => import('@/features/reports/productivity-workbench/ProductivityWorkbenchPage'),
);
// W3 decision (2026-07-05): one billing-transactions workbench replaces 3 PB report menu items.
const BillingWorkbenchPage = lazy(
  () => import('@/features/reports/billing-workbench/BillingWorkbenchPage'),
);
const PurgeTranPage = lazy(() => import('@/features/interfaces/purgetran/PurgeTranPage'));
// tester #1485: w_clear_ctr rebuilt as a dedicated page (docs/design/clear-ctr-rebuild.md);
// shadows the generic /browse/clear-ctr TableBrowser cfg.
const ResetCounterPage = lazy(() => import('@/features/admin/resetcounter/ResetCounterPage'));
// slice-1078: second parallel-agent workstation batch.
const TruckDispatchPage = lazy(() => import('@/features/truck/dispatch/TruckDispatchPage'));
// slice-3918 (#3690): PB w_truck_load — Truck Search's Load / Unload Truck icons.
const TruckLoadPage = lazy(() => import('@/features/truck/dispatch/TruckLoadPage'));
const TruckImportPage = lazy(() => import('@/features/truck/dispatch/TruckImportPage'));
const ConfigurationPage = lazy(() => import('@/features/config/configuration/ConfigurationPage'));
const ActivityUserGroupDetailPage = lazy(
  () => import('@/features/config/activityusergroup/ActivityUserGroupDetailPage'),
);
// #1473/#1474: PB w_domain_detail 3-pane zoom (From/To tag lists + checked pairs).
const DomainDetailPage = lazy(
  () => import('@/features/config/domaindetail/DomainDetailPage'),
);
// #1478: PB w_mask_detail form view (Validation / Display / Actions panes).
const MaskDetailPage = lazy(() => import('@/features/config/maskdetail/MaskDetailPage'));
const PclChargesPage = lazy(() => import('@/features/shipping/pclcharges/PclChargesPage'));
const CartonLookupRedirect = lazy(
  () => import('@/features/cartons/lookup/CartonLookupRedirect'),
);
const InventorySearchPage = lazy(() => import('@/features/inventory/search/InventorySearchPage'));
const ValueAddProcessPage = lazy(() => import('@/features/valueadd/process/ValueAddProcessPage'));
// slice-1079: third parallel-agent workstation batch.
const ValueAddPickPage = lazy(() => import('@/features/valueadd/pick/ValueAddPickPage'));
const MergePalletPage = lazy(() => import('@/features/cartons/mergepallet/MergePalletPage'));
const UpcCodesPage = lazy(() => import('@/features/items/upccodes/UpcCodesPage'));
const StateProductCodesPage = lazy(
  () => import('@/features/items/stateproductcode/StateProductCodesPage'),
);
const SubstitutionsPage = lazy(
  () => import('@/features/items/substitution/SubstitutionsPage'),
);
const VelocityHistoryPage = lazy(
  () => import('@/features/items/velocityhistory/VelocityHistoryPage'),
);
const HoldsPage = lazy(() => import('@/features/locationtype/holds/HoldsPage'));
const InvTypesPage = lazy(() => import('@/features/locationtype/invtype/InvTypesPage'));
const RecSortViolsPage = lazy(
  () => import('@/features/locationtype/recsortviol/RecSortViolsPage'),
);
const OwnerCarriersPage = lazy(
  () => import('@/features/owner/carriers/OwnerCarriersPage'),
);
const OwnerEmailsPage = lazy(
  () => import('@/features/owner/email/OwnerEmailsPage'),
);
const OwnerCountersPage = lazy(
  () => import('@/features/owner/counters/CountersPage'),
);
// slice-836: PB w_warehouse_ctr — per-warehouse counters (ctr_misc2).
const WarehouseCountersPage = lazy(
  () => import('@/features/warehouse/counters/CountersPage'),
);
const LabelCfgPage = lazy(
  () => import('@/features/warehouse/labelcfg/LabelCfgPage'),
);
const CustLblCodesPage = lazy(
  () => import('@/features/carrier/custlblcode/CustLblCodesPage'),
);
const QssiLblCodesPage = lazy(
  () => import('@/features/carrier/qssilblcode/QssiLblCodesPage'),
);
const CarrierServicesPage = lazy(
  () => import('@/features/carrier/service/CarrierServicesPage'),
);
const CustomerCarriersPage = lazy(
  () => import('@/features/customer/carriers/CustomerCarriersPage'),
);
const NotRetItemsPage = lazy(
  () => import('@/features/customer/notretitem/NotRetItemsPage'),
);
// slice-3111: w_customer_master_ref_field_mapping — Ship Carrier Ref PH Field.
const CustRefFieldMappingPage = lazy(
  () => import('@/features/customer-master/reffieldmapping/CustRefFieldMappingPage'),
);
const ShipToNotRetItemsPage = lazy(
  () => import('@/features/customer/shiptonotretitem/ShipToNotRetItemsPage'),
);
const ShipToCommentsPage = lazy(
  () => import('@/features/customer/shiptocomment/ShipToCommentsPage'),
);
const ShipMethodCustLblCodesPage = lazy(
  () => import('@/features/shipmethod/custlblcode/ShipMethodCustLblCodesPage'),
);
const ShipMethodQssiLblCodesPage = lazy(
  () => import('@/features/shipmethod/qssilblcode/ShipMethodQssiLblCodesPage'),
);
// slice-1628: PB w_ship_error SHIP_METHOD mode (w_shippingtables Methods tab).
const ShipMethodErrorCodesPage = lazy(
  () => import('@/features/shipmethod/errorcodes/ShipMethodErrorCodesPage'),
);
const LogCodesPage = lazy(
  () => import('@/features/actionlog/logcode/LogCodesPage'),
);
const BrandGroupsPage = lazy(
  () => import('@/features/brand/groups/BrandGroupsPage'),
);
// slice-2925 (#1660): w_brand_subgroup — the brand → group → subgroup zoom chain.
const BrandSubgroupsPage = lazy(
  () => import('@/features/brand/groups/BrandSubgroupsPage'),
);
const BomTypesPage = lazy(
  () => import('@/features/items/bomtype/BomTypesPage'),
);
const ChangeLogCodesPage = lazy(
  () => import('@/features/changelog/logcode/ChangeLogCodesPage'),
);
const EmailDistPage = lazy(
  () => import('@/features/email/distlistdet/EmailDistPage'),
);
const HarmonizedCountryPage = lazy(
  () => import('@/features/harmonized/country/HarmonizedCountryPage'),
);
const LoadPlanDetailPage = lazy(
  () => import('@/features/loadplan/detail/LoadPlanDetailPage'),
);
const CategoryTablesPage = lazy(
  () => import('@/features/genrpt/categorytables/CategoryTablesPage'),
);
const InstructionsPage = lazy(
  () => import('@/features/notification/instruction/InstructionsPage'),
);
const SectionLimitsPage = lazy(
  () => import('@/features/loadplan/sectionlimit/SectionLimitsPage'),
);
const PaGrpClassesPage = lazy(
  () => import('@/features/pagrp/classes/PaGrpClassesPage'),
);
const FavoritesPage = lazy(
  () => import('@/features/users/favorites/FavoritesPage'),
);
const OrderAssignPage = lazy(
  () => import('@/features/orders/orderassign/OrderAssignPage'),
);
const PickGrpClassesPage = lazy(
  () => import('@/features/pickgrp/classes/PickGrpClassesPage'),
);
const RstkGrpClassesPage = lazy(
  () => import('@/features/rstkgrp/classes/RstkGrpClassesPage'),
);
const ErrorCodesPage = lazy(
  () => import('@/features/carrier/errorcodes/ErrorCodesPage'),
);
const WaveTypePickRulesPage = lazy(
  () => import('@/features/wavetype/pickrule/PickRulesPage'),
);
const WhsEmailsPage = lazy(
  () => import('@/features/warehouse/email/WhsEmailsPage'),
);
const WhsCartonTypesPage = lazy(
  () => import('@/features/warehouse/cartontype/WhsCartonTypesPage'),
);
// #2537/#2538: PB w_warehouse_ship_method — assigned-checkbox grid over dbxx.ship_method.
const WarehouseShipMethodsPage = lazy(
  () => import('@/features/warehouse/shipmethod/WarehouseShipMethodsPage'),
);
const SourceLocationsPage = lazy(
  () => import('@/features/production/sourcelocation/SourceLocationsPage'),
);
const ReceiveLocationsPage = lazy(
  () => import('@/features/production/receivelocation/ReceiveLocationsPage'),
);
const JobZonesPage = lazy(
  () => import('@/features/job/zones/JobZonesPage'),
);
// #2436 (ANDREW "Productivity standard Icon is missing"): PB w_productivity_standard, the
// job-mode-only "&Productivity Standard" drill off w_productivity_config
// (srw:29 registers, srw:43 enables). Never ported before this slice.
const JobProductivityStandardPage = lazy(
  () => import('@/features/job/productivitystandard/JobProductivityStandardPage'),
);
const PutStationsPage = lazy(
  () => import('@/features/wavetype/putstation/PutStationsPage'),
);
const BomPage = lazy(() => import('@/features/items/bom/BomPage'));
const BomNotesPage = lazy(() => import('@/features/items/bom/BomNotesPage'));
const OwnerShipMethodsPage = lazy(
  () => import('@/features/owner/shipmethod/OwnerShipMethodsPage'),
);
const GroupLogTypesPage = lazy(
  () => import('@/features/changelog/grouptype/GroupLogTypesPage'),
);
const GroupUsersPage = lazy(
  () => import('@/features/usergroup/users/GroupUsersPage'),
);
const ModuleGroupsPage = lazy(
  () => import('@/features/module/groups/ModuleGroupsPage'),
);
const ItemInvTypesPage = lazy(
  () => import('@/features/items/invtype/ItemInvTypesPage'),
);
const ItemEquipmentPage = lazy(
  () => import('@/features/items/equipment/ItemEquipmentPage'),
);
const CustomerShiptoCarriersPage = lazy(
  () => import('@/features/customer/shiptocarriers/CustomerShiptoCarriersPage'),
);
const RefFieldMappingPage = lazy(
  () => import('@/features/owner/reffieldmapping/RefFieldMappingPage'),
);
const TranslationsPage = lazy(
  () => import('@/features/language/translations/TranslationsPage'),
);
const ColumnTranslatePage = lazy(
  () => import('@/features/language/translations/ColumnTranslatePage'),
);
// cfg-pa-rule rebuild: PB w_putprio / w_pickprio / w_restprio steps editors +
// the w_clone (rule-*) Clone Rule form, drilled from /browse/cfg-{pa,pick,rstk}-rule.
const RuleStepsPage = lazy(() => import('@/features/config/parules/RuleStepsPage'));
const CloneRulePage = lazy(() => import('@/features/config/parules/CloneRulePage'));
const MessageTranslationPage = lazy(
  () => import('@/features/language/messagetranslation/MessageTranslationPage'),
);
const RfMessageTranslationPage = lazy(
  () => import('@/features/language/rfmessagetranslation/RfMessageTranslationPage'),
);
const AdjustSearchPage = lazy(
  () => import('@/features/inventory/adjustsearch/AdjustSearchPage'),
);
const FormatDetailsPage = lazy(
  () => import('@/features/fieldformat/details/FormatDetailsPage'),
);
const AddressMappingPage = lazy(
  () => import('@/features/carrier/addressmapping/AddressMappingPage'),
);
const ZoneLocationsPage = lazy(
  () => import('@/features/zone/locations/ZoneLocationsPage'),
);
// slice-2955: real w_zone_usr / w_zone_device zooms (were cfg placeholders).
const ZoneUsersPage = lazy(() => import('@/features/zone/users/ZoneUsersPage'));
const ZoneDevicesPage = lazy(() => import('@/features/zone/devices/ZoneDevicesPage'));
const AutoHoldSetsPage = lazy(
  () => import('@/features/owner/autoholdset/AutoHoldSetsPage'),
);
const MoreColsPage = lazy(
  () => import('@/features/changelog/morecols/MoreColsPage'),
);
const ItemConfigurationsPage = lazy(
  () => import('@/features/items/configuration/ItemConfigurationsPage'),
);
const RfMessagesPage = lazy(
  () => import('@/features/language/rfmessages/RfMessagesPage'),
);
const DivisionCarriersPage = lazy(
  () => import('@/features/division/carriers/DivisionCarriersPage'),
);
const LaborTasksPage = lazy(
  () => import('@/features/labor/tasks/LaborTasksPage'),
);
const LaborTaskCreatePage = lazy(
  () => import('@/features/labor/tasks/LaborTaskCreatePage'),
);
const LaborUsersPage = lazy(
  () => import('@/features/labor/users/LaborUsersPage'),
);
const KitRequestsPage = lazy(
  () => import('@/features/kit/requests/KitRequestsPage'),
);
const AqlDetailsPage = lazy(
  () => import('@/features/aql/details/AqlDetailsPage'),
);
const PackagingTypesPage = lazy(
  () => import('@/features/cartontype/packaging/PackagingTypesPage'),
);
const BrandCodePage = lazy(() => import('@/features/config/brand/BrandCodePage'));
const BrandGroupPage = lazy(() => import('@/features/config/brand/BrandGroupPage'));
const BrandSubgroupPage = lazy(
  () => import('@/features/config/brand/BrandSubgroupPage'),
);
const InvTypePage = lazy(() => import('@/features/config/invtype/InvTypePage'));
const TaxClassPage = lazy(() => import('@/features/config/taxclass/TaxClassPage'));
const ReportImagePage = lazy(
  () => import('@/features/configuration/reportimage/ReportImagePage'),
);
const RfModulesPage = lazy(
  () => import('@/features/config/rfmodules/RfModulesPage'),
);
const RoutingOrderTagPage = lazy(
  () => import('@/features/order-tag/routing/RoutingOrderTagPage'),
);
const ItemCfgBatchClonePage = lazy(
  () => import('@/features/config/itemcfgbatchclone/ItemCfgBatchClonePage'),
);
const LabelGenPage = lazy(() => import('@/features/labels/labelgen/LabelGenPage'));
const InboundTruckSearchPage = lazy(
  () => import('@/features/truck/inboundsearch/InboundTruckSearchPage'),
);
const LabelViewPage = lazy(() => import('@/features/labels/labelview/LabelViewPage'));
const PostDeductPage = lazy(() => import('@/features/production/postdeduct/PostDeductPage'));
const PostDeductHistoryPage = lazy(
  () => import('@/features/production/postdeduct/PostDeductHistoryPage'),
);
const WorkSearchPage = lazy(() => import('@/features/work/search/WorkSearchPage'));
const GlobalOrderLineMaintPage = lazy(
  () => import('@/features/orders/global-line-maint/GlobalOrderLineMaintPage'),
);
const OrderContainersPage = lazy(
  () => import('@/features/orders/containers/OrderContainersPage'),
);
const BillEventActivityPage = lazy(
  () => import('@/features/billing/event-activity/BillEventActivityPage'),
);
const BillEventPeriodicPage = lazy(
  () => import('@/features/billing/event-periodic/BillEventPeriodicPage'),
);
const AutoProcessServerMaintPage = lazy(
  () => import('@/features/warehouse/auto-process-server/AutoProcessServerMaintPage'),
);
// #1644: PB w_auto_process_timer — dedicated master-detail FORM (dw_1 list + dw_2 detail
// form) shadowing the generic /browse/:key grid the tester called "vastly different".
const AutoProcessTimerPage = lazy(
  () => import('@/features/warehouse/auto-process-timer/AutoProcessTimerPage'),
);
// batch-04 needs-rebuild: PB w_event_req_log is the master-detail w_dwtsheet_dbl (dw_1 list +
// dw_2 read-only detail FORM). Dedicated page shadows the generic /browse/:key grid, which
// never rendered the detail-form pane.
const EventLogPage = lazy(() => import('@/features/warehouse/event-log/EventLogPage'));
const OrderStatusSearchPage = lazy(
  () => import('@/features/interfaces/orderstatus/OrderStatusSearchPage'),
);
const ShipMaintenanceSearchPage = lazy(
  () => import('@/features/interfaces/shipmaint/ShipMaintenanceSearchPage'),
);
const BillTransactionSearchPage = lazy(
  () => import('@/features/billing/transaction-search/BillTransactionSearchPage'),
);
const ShipmentCostPage = lazy(() => import('@/features/items/shipment-cost/ShipmentCostPage'));
const TransactionMaintenanceSearchPage = lazy(
  () => import('@/features/interfaces/transactionmaint/TransactionMaintenanceSearchPage'),
);
const DeleteOpenOrdersPage = lazy(
  () => import('@/features/orders/delete-open/DeleteOpenOrdersPage'),
);
const ItemSearchPage = lazy(() => import('@/features/items/search/ItemSearchPage'));
const WaveStatusPage = lazy(() => import('@/features/orders/wavestatus/WaveStatusPage'));
const GlobalReceiptMaintPage = lazy(
  () => import('@/features/receipts/globalmaint/GlobalReceiptMaintPage'),
);
const ProdLotAssignPage = lazy(
  () => import('@/features/production/prod-lot-assign/ProdLotAssignPage'),
);
const LabelMappingPage = lazy(
  () => import('@/features/config/labelmapping/LabelMappingPage'),
);
const PrePackItemBuildPage = lazy(
  () => import('@/features/items/prepackbuild/PrePackItemBuildPage'),
);
const WindowTranslationPage = lazy(
  () => import('@/features/language/windowtranslation/WindowTranslationPage'),
);
const ShipDownloadSearchPage = lazy(
  () => import('@/features/interfaces/shipdownload/ShipDownloadSearchPage'),
);
const ManualPutawayPage = lazy(
  () => import('@/features/receipts/manualputaway/ManualPutawayPage'),
);
const TruckScheduleBoardPage = lazy(
  () => import('@/features/truck/truckschedule/TruckScheduleBoardPage'),
);
const UserMaintenancePage = lazy(
  () => import('@/features/users/maintenance/UserMaintenancePage'),
);
const ZonePage = lazy(() => import('@/features/config/zone/ZonePage'));
// slice-3007 (#2098): PB w_zone form-shape list (grouped headers) replaces the AG-grid
// render at /config/zones — docs/design/zones-rebuild.md.
const ZoneMasterListPage = lazy(() => import('@/features/config/zone/ZoneMasterListPage'));
const DomainPage = lazy(() => import('@/features/config/domain/DomainPage'));
const StatePage = lazy(() => import('@/features/config/state/StatePage'));
const LaborCodePage = lazy(
  () => import('@/features/config/laborcode/LaborCodePage'),
);
const TrailerTypePage = lazy(
  () => import('@/features/config/trailertype/TrailerTypePage'),
);
const ConversionPage = lazy(
  () => import('@/features/config/conversion/ConversionPage'),
);
const CountryPage = lazy(
  () => import('@/features/config/country/CountryPage'),
);
const ConsolidatorPage = lazy(
  () => import('@/features/config/consolidator/ConsolidatorPage'),
);
const DivisionPage = lazy(
  () => import('@/features/config/division/DivisionPage'),
);
const ThirdPartyPage = lazy(
  () => import('@/features/config/thirdparty/ThirdPartyPage'),
);
const BillCodePage = lazy(
  () => import('@/features/config/billcode/BillCodePage'),
);
const GlCodePage = lazy(
  () => import('@/features/config/glcode/GlCodePage'),
);
const NestingCodePage = lazy(
  () => import('@/features/config/nestingcode/NestingCodePage'),
);
const SalesRepPage = lazy(
  () => import('@/features/config/salesrep/SalesRepPage'),
);
const InvoiceCompanyPage = lazy(
  () => import('@/features/config/invoicecompany/InvoiceCompanyPage'),
);
const CombineCategoryPage = lazy(
  () => import('@/features/config/combinecategory/CombineCategoryPage'),
);
const TaskCodePage = lazy(
  () => import('@/features/config/taskcode/TaskCodePage'),
);
const ReceiptTypePage = lazy(
  () => import('@/features/config/receipttype/ReceiptTypePage'),
);
const CalcServerPage = lazy(
  () => import('@/features/config/calcserver/CalcServerPage'),
);
const ReceiptDefaultPage = lazy(
  () => import('@/features/config/receiptdefault/ReceiptDefaultPage'),
);
const OrderDefaultPage = lazy(
  () => import('@/features/config/orderdefault/OrderDefaultPage'),
);
const CycCategoryPage = lazy(
  () => import('@/features/config/cyccategory/CycCategoryPage'),
);
const AlarmTypePage = lazy(
  () => import('@/features/config/alarmtype/AlarmTypePage'),
);
const RtnGrpPage = lazy(
  () => import('@/features/config/rtngrp/RtnGrpPage'),
);
const WorkTypePage = lazy(
  () => import('@/features/config/worktype/WorkTypePage'),
);
const VaRulePage = lazy(
  () => import('@/features/config/varule/VaRulePage'),
);
const EmailGroupPage = lazy(
  () => import('@/features/config/emailgroup/EmailGroupPage'),
);
const NotifySetPage = lazy(
  () => import('@/features/config/notifyset/NotifySetPage'),
);
const PriceChangeGlobalPage = lazy(
  () => import('@/features/config/price-change-global/PriceChangeGlobalPage'),
);
// slice-2972: PB w_price_change_review is a freeform record sheet (stacked composite cells,
// changed-tiers-only price lines). Dedicated read-only page shadows the generic /browse/:key
// grid (the slice-2924 flattened 19-column port). docs/design/price-change-review-rebuild.md.
const PriceChangeReviewPage = lazy(
  () => import('@/features/config/price-change-review/PriceChangeReviewPage'),
);
const InvBalancePage = lazy(
  () => import('@/features/interfaces/inv-balance/InvBalancePage'),
);
const MovePalletPage = lazy(
  () => import('@/features/inventory/move-pallet/MovePalletPage'),
);
const MovePiecesFrLocationPage = lazy(
  () => import('@/features/inventory/move-pieces-fr-location/MovePiecesFrLocationPage'),
);
const MovePiecesFrPalletPage = lazy(
  () => import('@/features/inventory/move-pieces-fr-pallet/MovePiecesFrPalletPage'),
);
const MovePiecesFrAssignPage = lazy(
  () => import('@/features/inventory/move-pieces-fr-assign/MovePiecesFrAssignPage'),
);
// #849 port wave 1: w_move_pieces_fr_carton (carton sibling of the move-pieces family).
const MovePiecesFrCartonPage = lazy(
  () => import('@/features/inventory/move-pieces-fr-carton/MovePiecesFrCartonPage'),
);
const MoversWsPage = lazy(() => import('@/features/movers/MoversWsPage'));
const MoversRfPage = lazy(() => import('@/features/movers/MoversRfPage'));
const RfScreensCurrentPage = lazy(
  () => import('@/features/movers/RfScreensCurrentPage'),
);
const RfScreensHistoryPage = lazy(
  () => import('@/features/movers/RfScreensHistoryPage'),
);
const RfScreensHistoryViewPage = lazy(
  () => import('@/features/movers/RfScreensHistoryViewPage'),
);
const Fm11Page = lazy(() => import('@/features/rewarehousing/fm11/Fm11Page'));
const GlobalVelocityUpdatePage = lazy(
  () => import('@/features/rewarehousing/velocity/GlobalVelocityUpdatePage'),
);
const GlobalLocationAssignmentPage = lazy(
  () => import('@/features/rewarehousing/locassign/GlobalLocationAssignmentPage'),
);
const CholdPage = lazy(() => import('@/features/rewarehousing/chold/CholdPage'));
const FixItemLocationPage = lazy(
  () => import('@/features/admin/fixitemloc/FixItemLocationPage'),
);
const WorkGrabStatusPage = lazy(
  () => import('@/features/work/grabstatus/WorkGrabStatusPage'),
);
const BatchPaperPickStartPage = lazy(
  () => import('@/features/orders/paperpick/BatchPaperPickStartPage'),
);
const BatchPaperPickFinishPage = lazy(
  () => import('@/features/orders/paperpick/BatchPaperPickFinishPage'),
);
const GenPutawaySheetsPage = lazy(
  () => import('@/features/receipts/genputawaysheets/GenPutawaySheetsPage'),
);
const FastTrackPutawayPage = lazy(
  () => import('@/features/receipts/fasttrackput/FastTrackPutawayPage'),
);
const GenLoadSheetsPage = lazy(
  () => import('@/features/orders/genloadsheets/GenLoadSheetsPage'),
);
const GenRestockSheetsPage = lazy(
  () => import('@/features/restocking/genrestocksheets/GenRestockSheetsPage'),
);
const ProductionReconcilePage = lazy(
  () => import('@/features/production/reconcile/ProductionReconcilePage'),
);
const ConvertManualPage = lazy(
  () => import('@/features/work/convertmanual/ConvertManualPage'),
);
const QcQuickReleasePage = lazy(
  () => import('@/features/receipts/qcquickrelease/QcQuickReleasePage'),
);
const QcPalCtnReleasePage = lazy(
  () => import('@/features/receipts/qcpalctnrelease/QcPalCtnReleasePage'),
);
const QcMoreInventoryPage = lazy(
  () => import('@/features/receipts/qcmoreinventory/QcMoreInventoryPage'),
);
const ReturnsOrderPage = lazy(
  () => import('@/features/receipts/returnsorder/ReturnsOrderPage'),
);
const PaSortByItemPage = lazy(
  () => import('@/features/putawaysort/byitem/PaSortByItemPage'),
);
const PaSortByZonePage = lazy(
  () => import('@/features/putawaysort/byzone/PaSortByZonePage'),
);
const RcvPaSortByZonePage = lazy(
  () => import('@/features/putawaysort/rcvbyzone/RcvPaSortByZonePage'),
);
const ReturnsPickbinPage = lazy(
  () => import('@/features/receipts/returnscan/ReturnsPickbinPage'),
);
const ReturnsTotePage = lazy(
  () => import('@/features/receipts/returnscan/ReturnsTotePage'),
);
const ReturnsProductPage = lazy(
  // slice-1079: repointed from the returnscan stub to the full returnsproduct station.
  () => import('@/features/receipts/returnsproduct/ReturnsProductPage'),
);
const ContentsMaintPage = lazy(
  () => import('@/features/inventory/contentsmaint/ContentsMaintPage'),
);
const ReceiveProductPage = lazy(
  () => import('@/features/receipts/receiveproduct/ReceiveProductPage'),
);
const MinMaxImportPage = lazy(
  () => import('@/features/admin/imports/minmax/MinMaxImportPage'),
);
// slice-3427 (ADR-0043): PB w_import_data — web-native import wizard (upload → map →
// validate-everything → one-transaction commit). Importer #1: Item Master.
const ImportDataPage = lazy(
  () => import('@/features/admin/imports/data/ImportDataPage'),
);
const InventoryImportPage = lazy(
  () => import('@/features/admin/imports/inventory/InventoryImportPage'),
);
const BolReportPage = lazy(() => import('@/features/reports/BolReportPage'));
const WaveReportPage = lazy(() => import('@/features/reports/WaveReportPage'));
const CycReportPage = lazy(() => import('@/features/reports/CycReportPage'));
// Slice-420 (BIG): full workflow port of w_rep_cyc.
const GenerateCycleCountsPage = lazy(
  () => import('@/features/cyc/generate/GenerateCycleCountsPage'),
);
// Slice-427: w_cyc_master maintenance page (was misclassified browse).
const CycMasterPage = lazy(() => import('@/features/cyc/master/CycMasterPage'));
// Slice-427: w_inv_back_to_stock data-entry workflow (was misclassified browse).
const InvBackToStockPage = lazy(
  () => import('@/features/inventory/back-to-stock/InvBackToStockPage'),
);
// Slice-430: w_pallet_back_to_stock data-entry workflow (sister to inv-back-to-stock).
const PalletBackToStockPage = lazy(
  () => import('@/features/inventory/back-to-stock/PalletBackToStockPage'),
);
// Slice-455: PB w_receipt_maint INSERT mode -- New Receipt create form.
const ReceiptCreatePage = lazy(
  () => import('@/features/receipts/create/ReceiptCreatePage'),
);
// Slice-800: PB w_item_master INSERT mode is now served by ItemMasterPage at
// `/items/master` (no params). The legacy slice-456 minimal ItemCreatePage was retired
// because the operator needed all ~150 dw_item_master_main fields, not 26 -- see
// docs/dev-rules-slice-800-notes if you're hunting for the missing import. The
// /items/new route below redirects to /items/master so any cached toolbar / drilldown
// links keep working.
// Slice-457: PB w_customer_master INSERT mode -- New Customer create form.
const CustomerCreatePage = lazy(
  () => import('@/features/customer-master/create/CustomerCreatePage'),
);
// slice-1614 (#1302/#1303): PB w_customer_shipto '(new)' -- New Customer Ship To form.
const CustomerShipToCreatePage = lazy(
  () => import('@/features/customers/create/CustomerShipToCreatePage'),
);
// Slice-458: PB w_vendor INSERT mode -- New Vendor create form.
const VendorCreatePage = lazy(
  () => import('@/features/vendor/create/VendorCreatePage'),
);
// Slice-459: PB w_location INSERT mode -- New Location create form.
const LocationCreatePage = lazy(
  () => import('@/features/location/create/LocationCreatePage'),
);
// Slice-460: PB w_carrier INSERT mode -- New Carrier create form.
const CarrierCreatePage = lazy(
  () => import('@/features/carrier/create/CarrierCreatePage'),
);
// Slice-461: PB w_ship_method INSERT mode -- New Ship Method create form.
const ShipMethodCreatePage = lazy(
  () => import('@/features/shipmethod/create/ShipMethodCreatePage'),
);
// Slice-462: PB w_owner INSERT mode -- New Owner create form.
const OwnerCreatePage = lazy(
  () => import('@/features/owner/create/OwnerCreatePage'),
);
// slice-832: PB w_owner EDIT mode — 9-tab maintenance of one owner record.
const OwnerMaintenancePage = lazy(
  () => import('@/features/owner/maintenance/OwnerMaintenancePage'),
);
// Slice-463: PB w_warehouse INSERT mode -- New Warehouse create form.
const WarehouseCreatePage = lazy(
  () => import('@/features/warehouse/create/WarehouseCreatePage'),
);
// slice-831: PB w_warehouse EDIT mode — 12-tab maintenance of one warehouse record.
const WarehouseMaintenancePage = lazy(
  () => import('@/features/warehouse/maintenance/WarehouseMaintenancePage'),
);
// Slice-464: PB w_reason_* INSERT mode -- 6 reason-code create forms share a page.
const ReasonCodeCreatePage = lazy(
  () => import('@/features/reasoncode/create/ReasonCodeCreatePage'),
);
// Slice-465: PB simple-config INSERT mode -- 10 key+description create forms share a page.
const SimpleConfigCreatePage = lazy(
  () => import('@/features/simpleconfig/create/SimpleConfigCreatePage'),
);
// Slice-466: PB medium-config INSERT mode -- 8 multi-field create forms share a page.
const MediumConfigCreatePage = lazy(
  () => import('@/features/mediumconfig/create/MediumConfigCreatePage'),
);
const ProductionInOutReportPage = lazy(
  () => import('@/features/reports/ProductionInOutReportPage'),
);
const DashboardPage = lazy(() => import('@/features/reports/DashboardPage'));
const WebReportPage = lazy(() => import('@/features/reports/WebReportPage'));
const LabelCentralPage = lazy(() => import('@/features/labels/LabelCentralPage'));
const ReprintLabelPage = lazy(() => import('@/features/labels/reprint/ReprintLabelPage'));
const LaborTaskSearchPage = lazy(
  () => import('@/features/labor/tasksearch/LaborTaskSearchPage'),
);
const LabelPrintStdPage = lazy(() => import('@/features/labels/LabelPrintStdPage'));
const VaItemLabelingPage = lazy(() => import('@/features/labels/VaItemLabelingPage'));
const VaItemBulkLabelingPage = lazy(
  () => import('@/features/labels/VaItemBulkLabelingPage'),
);
const KitBuildingSerialPage = lazy(
  () => import('@/features/labels/KitBuildingSerialPage'),
);
const KitBuildingMultiSerLotPage = lazy(
  () => import('@/features/labels/KitBuildingMultiSerLotPage'),
);
const GlobalLocationMaintPage = lazy(
  () => import('@/features/inventory/globalmaint/GlobalLocationMaintPage'),
);
const ArchiveAncientPage = lazy(
  () => import('@/features/admin/ancient/ArchiveAncientPage'),
);
const ApparelItemBuildPage = lazy(
  () => import('@/features/config/itembuild/ApparelItemBuildPage'),
);
const QuickLabelLoadPage = lazy(
  () => import('@/features/config/quicklabel/QuickLabelLoadPage'),
);
const MatCutFastPickPage = lazy(
  () => import('@/features/activities/matcut/MatCutFastPickPage'),
);
const MatCutRemainFastPickPage = lazy(
  () => import('@/features/activities/matcut/MatCutRemainFastPickPage'),
);
const QuickShipPage = lazy(
  () => import('@/features/orders/quickship/QuickShipPage'),
);
// slice-4913: PB w_quickpick (Quick Pick Confirmation) — was a /pb placeholder.
const QuickPickPage = lazy(
  () => import('@/features/orders/quickpick/QuickPickPage'),
);
const PurgeOwnerPage = lazy(
  () => import('@/features/admin/purgeowner/PurgeOwnerPage'),
);
const PurgePage = lazy(() => import('@/features/admin/purge/PurgePage'));
const PrintLogPage = lazy(() => import('@/features/admin/printlog/PrintLogPage'));
const ProductionBigBoardPage = lazy(
  () => import('@/features/production/bigboard/ProductionBigBoardPage'),
);
const OrderStatusDashboardPage = lazy(
  () => import('@/features/orders/statusdashboard/OrderStatusDashboardPage'),
);
const ManualStockListBrowsePage = lazy(
  () => import('@/features/restock/manualstocklist/ManualStockListBrowsePage'),
);
const ManualStockListDetailPage = lazy(
  () => import('@/features/restock/manualstocklist/ManualStockListDetailPage'),
);
const MaterialCutPage = lazy(
  () => import('@/features/material/cut/MaterialCutPage'),
);
const ManualBillingPage = lazy(
  () => import('@/features/billing/manualbilling/ManualBillingPage'),
);
const VendorQcUpdatePage = lazy(
  () => import('@/features/vendor/qcupdate/VendorQcUpdatePage'),
);
const OrderCreatePage = lazy(
  () => import('@/features/orders/create/OrderCreatePage'),
);
const OrderLinesPage = lazy(
  () => import('@/features/orders/lines/OrderLinesPage'),
);
const WorkPoolPage = lazy(() => import('@/features/workpool/WorkPoolPage'));
// Work-pool consolidation (docs/design/work-pool-consolidation.md): the old
// /browse/work-pool TableBrowser half redirects to the canonical /manage/work-pool,
// mapping its drill query params onto the select-by modes. Static path shadows the
// generic /browse/:key route.
const WorkPoolBrowseRedirect = lazy(
  () => import('@/features/workpool/WorkPoolBrowseRedirect'),
);
const WorkPoolUserAssignedPage = lazy(
  () => import('@/features/workpool/WorkPoolUserAssignedPage'),
);
const WorkPoolDetailPage = lazy(
  () => import('@/features/workpool/WorkPoolDetailPage'),
);
// #2481: PB w_work_pool_wc — the Ship Labels Lookup target for w_work_pool /
// w_work_pool_detail (ue_cart). Carton Lookup is its zoom CHILD, not this.
const WorkPoolShipLabelsPage = lazy(
  () => import('@/features/workpool/WorkPoolShipLabelsPage'),
);
const PicksPage = lazy(() => import('@/features/picks/PicksPage'));
const CloneDefaultPage = lazy(
  () => import('@/features/config/clonedefault/CloneDefaultPage'),
);
const PutwallBatchPage = lazy(
  () => import('@/features/orders/putwall-batch/PutwallBatchPage'),
);
const ReceiptLineSearchPage = lazy(
  () => import('@/features/receipts/line-search/ReceiptLineSearchPage'),
);
const PutawayErrorsPage = lazy(
  () => import('@/features/workpool/putawayerrors/PutawayErrorsPage'),
);
// slice-799: PB w_truck_picklocsum read-only summary; opened from truck-activity
// callers via /truck-picklocsum?warehouseId=...&truckId=...
const TruckPickLocSumPage = lazy(
  () => import('@/features/truck/picklocsum/TruckPickLocSumPage'),
);
const NewPreReceiptPage = lazy(
  () => import('@/features/receipts/prereceipt/NewPreReceiptPage'),
);
const InventoryInquiryPage = lazy(
  () => import('@/features/inventory/inquiry/InventoryInquiryPage'),
);
const TableBrowserPage = lazy(() => import('@/features/browse/TableBrowserPage'));
// slice-814: PB w_qc_rate is 1 window / 4 tabs — single tabbed host page.
const TabbedQcRatePage = lazy(() => import('@/features/qc-rate/TabbedQcRatePage'));
// slice-815: PB w_shippingtables is 1 window / 19 tabs — single tabbed host page.
// slice-3626 (ADR-0050): customer-DEMO-only Slingshot POC screens (docs/bringlaborover.txt).
// Lazy so recharts/mqtt only download on a demo box that actually opens them.
const FloorPulsePage = lazy(() => import('@/features/reports/floor-pulse/FloorPulsePage').then((m) => ({ default: m.FloorPulsePage })));
const LaborByUserPage = lazy(() => import('@/features/reports/labor-by-user/LaborByUserPage').then((m) => ({ default: m.LaborByUserPage })));
const LaborUphPage = lazy(() => import('@/features/reports/labor-uph/LaborUphPage').then((m) => ({ default: m.LaborUphPage })));
const MonthlyKpiPage = lazy(() => import('@/features/reports/monthly-kpi/MonthlyKpiPage').then((m) => ({ default: m.MonthlyKpiPage })));
const ShiftRecapPage = lazy(() => import('@/features/reports/shift-recap/ShiftRecapPage').then((m) => ({ default: m.ShiftRecapPage })));
const UserScorecardPage = lazy(() => import('@/features/reports/user-scorecard/UserScorecardPage').then((m) => ({ default: m.UserScorecardPage })));
const UserTravelPage = lazy(() => import('@/features/reports/user-travel/UserTravelPage').then((m) => ({ default: m.UserTravelPage })));
const LaborTaskAssignmentPage = lazy(() => import('@/features/reports/labor-assign/LaborTaskAssignmentPage').then((m) => ({ default: m.LaborTaskAssignmentPage })));
// slice-3634 (docs/design/work-visualization.md): demo-only overhead map + work heatmap.
const WorkVisualizationPage = lazy(() => import('@/features/reports/work-visualization/WorkVisualizationPage').then((m) => ({ default: m.WorkVisualizationPage })));
// slice-3639 (docs/design/warehouse-status.md): demo-only KPI / attention board, filed under
// Manage Warehouse rather than Reports (Chris 2026-09-13).
const WarehouseStatusPage = lazy(() => import('@/features/reports/warehouse-status/WarehouseStatusPage').then((m) => ({ default: m.WarehouseStatusPage })));
const RfSimulatorPage = lazy(() => import('@/features/tools/rf-simulator/RfSimulatorPage').then((m) => ({ default: m.RfSimulatorPage })));
const TabbedShippingTablesPage = lazy(
  () => import('@/features/shipping-tables/TabbedShippingTablesPage'),
);
// slice-3351 (#2878/#2929): w_shippingtables' two special-services Zoom children,
// dedicated pages shadowing the generic /browse/:key (see the route comment).
const SpecialServicesForServicePage = lazy(
  () => import('@/features/shipping-tables/SpecialServicesForServicePage'),
);
const SpecialServicesForShipViaPage = lazy(
  () => import('@/features/shipping-tables/SpecialServicesForShipViaPage'),
);
// slice-816: PB w_production_status is 1 window / 6 inquiry tabs.
const TabbedProductionStatusPage = lazy(
  () => import('@/features/production-status/TabbedProductionStatusPage'),
);
// slice-817: PB w_report_types is 1 window / 3 tabs partitioning report_type.
const TabbedReportTypesPage = lazy(
  () => import('@/features/report-types/TabbedReportTypesPage'),
);
// slice-818: PB w_item_master_default is 1 window / 5 tabs on one item_master row.
const TabbedItemMasterDefaultPage = lazy(
  () => import('@/features/items/default-maintenance/TabbedItemMasterDefaultPage'),
);
// slice-819: PB w_qc_check (QC Inspect) — full operator workflow rebuild.
const QcInspectPage = lazy(() => import('@/features/qc/inspect/QcInspectPage'));
// slice-3925 (#3736): PB w_qc_check_truck (QC Inspect from Truck Search / Truck Maintenance).
// Drill-only window (window_modules menu_tag None) — router-only, like /browse/truck-load.
const TruckQcInspectPage = lazy(() => import('@/features/qc/inspect/TruckQcInspectPage'));
// slice-820: PB w_alarms is 1 window / 2 tabs partitioning alarm.aggregate_flag.
const TabbedAlarmsPage = lazy(() => import('@/features/alarms/TabbedAlarmsPage'));
// #481 (tester ANDREW): PB w_crt_language is 1 window / 2 tabs (Work Station + Base).
const TabbedCrtLanguagePage = lazy(
  () => import('@/features/language/TabbedCrtLanguagePage'),
);
// #483 (tester ANDREW): PB w_phfc_translate_menu is 1 window / 2 tabs (System Menu + Window Menu)
// with a shared Language dropdown. Static path shadows /browse/:key.
const TabbedMenuTranslatePage = lazy(
  () => import('@/features/language/TabbedMenuTranslatePage'),
);
// #535 (tester ANDREW): PB w_pick_doc_form_lbl is the pick-doc-form Zoom child — 1 window /
// 2 tabs (Label Document form + Report Document form) keyed by pick_doc_form_id.
const TabbedPickDocFormPage = lazy(
  () => import('@/features/browse/TabbedPickDocFormPage'),
);
// #1532/#1543/#1542/#1544: PB w_work_mode_type is an assignment matrix (all work types +
// Assigned checkbox), not a flat CRUD grid. Dedicated page shadows the generic /browse/:key.
const WorkModeTypePage = lazy(() => import('@/features/browse/WorkModeTypePage'));
// slice-3138 (Chris GO 7-28 #3): PB w_import_sub_po_lines is a profile-driven import
// WORKBENCH (7 registered icons, runtime-rebuilt grid, prep/perf packet SPs), not a flat
// CRUD grid. Dedicated page shadows the generic /browse/:key cfg (slice-768).
const ImportSubPoLinesPage = lazy(
  () => import('@/features/admin/imports/subpolines/ImportSubPoLinesPage'),
);
// #1608: PB w_combine_category_detail is a membership matrix (every item_class + an "Allow"
// checkbox), not a flat CRUD grid. Dedicated page shadows the generic /browse/:key.
const CombineCategoryDetailPage = lazy(
  () => import('@/features/browse/CombineCategoryDetailPage'),
);
// slice-3017 (#2131 #1267): PB w_ph_obj_document — the SHARED per-object Documents window
// (dynamic "Documents for VA Rule: X" title, Description/File Name + per-row Browse upload,
// zoom-opens-document). Dedicated page shadows the generic /browse/:key grid.
const PhObjDocumentPage = lazy(
  () => import('@/features/system/objdocument/PhObjDocumentPage'),
);
// slice-821: PB w_reasons is 1 window / 7 tabs over different reason_* tables.
const TabbedReasonsPage = lazy(() => import('@/features/reasons/TabbedReasonsPage'));
// slice-822: PB w_lock is 1 window / 8 tabs over locks + 4 related tables.
const TabbedLockPage = lazy(() => import('@/features/lock/TabbedLockPage'));
// slice-823: PB w_container_type is 1 window / 6 tabs over container-type tables.
const TabbedContainerTypePage = lazy(
  () => import('@/features/container-type/TabbedContainerTypePage'),
);
// slice-898: full-field edit pages for pallet_type / carton_type rows
// (issue #359: Pallet/Carton tabs missing Dim Fields, UOM, Calculate icons).
const PalletTypeEditPage = lazy(
  () => import('@/features/container-type/PalletTypeEditPage'),
);
const CartonTypeEditPage = lazy(
  () => import('@/features/container-type/CartonTypeEditPage'),
);
// slice-824: PB w_route_detail is 1 window / 4 tabs over route-detail partition tables.
const TabbedRouteDetailPage = lazy(
  () => import('@/features/route-detail/TabbedRouteDetailPage'),
);
// slice-825: PB w_item_class_detail is 1 window / 5 tabs over one item_class row.
const TabbedItemClassDetailPage = lazy(
  () => import('@/features/item-class-detail/TabbedItemClassDetailPage'),
);
// PB w_item_class_d_cfg: single-record freeform "Default Configuration for Item Class"
// over item_class_default_cfg (was misrepresented as the AG-grid browse).
const ItemClassDefaultCfgPage = lazy(
  () => import('@/features/item-class-default-cfg/ItemClassDefaultCfgPage'),
);
// slice-826: PB w_usr_grp_det is the 4-tab tag/untag assignment window for user groups.
const TabbedUserGroupDetailPage = lazy(
  () => import('@/features/user-group-detail/TabbedUserGroupDetailPage'),
);
// slice-828: PB w_hi_orddata is the 5-tab "Shipped Product History for Order" inquiry.
const TabbedShippedHistoryPage = lazy(
  () => import('@/features/shipped-history/TabbedShippedHistoryPage'),
);
// slice-829: PB w_truck_status_detail is the 7-tab truck inquiry opened from w_truck_status.
const TabbedTruckStatusDetailPage = lazy(
  () => import('@/features/truck-status-detail/TabbedTruckStatusDetailPage'),
);
// slice-3126: PB w_trk — outbound Truck Maintenance (header free-form + Schedule/BOL/
// Ship To Address/Custom/Comment tabs over the shared truck_header row).
const TruckMaintenancePage = lazy(
  () => import('@/features/truck/maintenance/TruckMaintenancePage'),
);
// #2414: PB w_truckinbound — Inbound Truck Maintenance (the i-trk arm of
// w_truck_inout_search's ue_zoom; o-trk keeps w_trk / TruckMaintenancePage above).
const InboundTruckMaintenancePage = lazy(
  () => import('@/features/truck/inboundmaintenance/InboundTruckMaintenancePage'),
);
// slice-830: PB w_wave_det is the 7-tab wave inquiry opened from w_waves (Wave Status).
const TabbedWaveDetailPage = lazy(
  () => import('@/features/wave-detail/TabbedWaveDetailPage'),
);
// slice-3097: PB w_wave_load_plan — the Wave Load Plan drag-drop truck-loading board (Chris
// approved 2026-07-26). Replaces the interim read-only /browse/wave-load-plan-detail grid
// (slice-3088) as the Zoom target from /browse/wave-load-plans.
const LoadPlanBoardPage = lazy(
  () => import('@/features/orders/loadplan/LoadPlanBoardPage'),
);
const PbPlaceholderPage = lazy(() => import('@/features/pb-placeholder/PbPlaceholderPage'));
const VendorMaintenancePage = lazy(
  () => import('@/features/vendor/maintenance/VendorMaintenancePage'),
);
const LocationMaintenancePage = lazy(
  () => import('@/features/location/maintenance/LocationMaintenancePage'),
);
const LocationStatusPage = lazy(
  () => import('@/features/location/status/LocationStatusPage'),
);
const CustomerMasterMaintenancePage = lazy(
  () => import('@/features/customer-master/maintenance/CustomerMasterMaintenancePage'),
);
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const PbReportRunnerPage = lazy(() => import('@/features/reports/pbrunner/PbReportRunnerPage'));
// #3034/#3035 (slice-3468): rpt-wave-estimate is NOT a generic one-field-per-SP-param
// report — PB's w_pre_report (wave_estimate) is a two-pane criteria window with a taggable
// wave list. It keeps the same /reports/pb/rpt-wave-estimate route (hub + nav + the
// Wave Status drill all point at it) via a more-specific route declared before the
// :reportKey catch-all.
const ShipmentAnalysisByWavePage = lazy(
  () => import('@/features/reports/waveestimate/ShipmentAnalysisByWavePage'),
);
const AskMePage = lazy(() => import('@/features/reports/askme/AskMePage'));
// slice-3886 (ADR-0064): Report Builder on the PowerHouse genrpt metadata.
const ReportBuilderPage = lazy(() => import('@/features/reports/builder/ReportBuilderPage'));
const ChangePasswordPage = lazy(() => import('@/features/auth/profile/ChangePasswordPage'));
const ProductivitySwitcherPage = lazy(() => import('@/features/auth/profile/ProductivitySwitcherPage'));
const FeedbackQueuePage = lazy(() => import('@/features/feedback/FeedbackQueuePage'));
const SecurityConfigPage = lazy(() => import('@/features/config/security/SecurityConfigPage'));
const CartonActivityLauncherPage = lazy(
  () => import('@/features/cartons/activity-launcher/CartonActivityLauncherPage'),
);
const CartonDocReprintPage = lazy(
  () => import('@/features/cartons/docreprint/CartonDocReprintPage'),
);
const CartonMergeShipPage = lazy(
  () => import('@/features/cartons/merge-ship/CartonMergeShipPage'),
);
const CartonOverpackShipPage = lazy(
  () => import('@/features/cartons/overpack-ship/CartonOverpackShipPage'),
);
const CartonBorderfreeShipPage = lazy(
  () => import('@/features/cartons/borderfree-ship/CartonBorderfreeShipPage'),
);
const CartonRecombineShipPage = lazy(
  () => import('@/features/cartons/recombine-ship/CartonRecombineShipPage'),
);
const PutToStorePage = lazy(
  () => import('@/features/cartons/put-to-store/PutToStorePage'),
);
const CartonProcessPage = lazy(
  () => import('@/features/cartons/process/CartonProcessPage'),
);
// #849 port wave 1: w_carton_induction (scan-driven carton/tote induction console).
const CartonInductionPage = lazy(
  () => import('@/features/cartons/induction/CartonInductionPage'),
);
const CartonRepackPage = lazy(
  () => import('@/features/cartons/repack/CartonRepackPage'),
);
const CartonPickpackShipSioPage = lazy(
  () => import('@/features/cartons/pickpack-ship-sio/CartonPickpackShipSioPage'),
);
const CartonLookupPage = lazy(() => import('@/features/cartons/lookup/CartonLookupPage'));
const CartonPickpackShipPage = lazy(
  () => import('@/features/cartons/pickpack-ship/CartonPickpackShipPage'),
);
const ShipShippingPage = lazy(
  () => import('@/features/shipping/shipping/ShipShippingPage'),
);
const CartonPickverifyShipPage = lazy(
  () => import('@/features/cartons/pickverify-ship/CartonPickverifyShipPage'),
);
const CartonRepack1MPage = lazy(
  () => import('@/features/cartons/repack-1-m/CartonRepack1MPage'),
);
// slice-3013 (§4.23): legacy folded ?mode= route → the two mode-locked routes.
const LegacyRepack1MRedirect = lazy(
  () => import('@/features/cartons/repack-1-m/LegacyRepack1MRedirect'),
);
const CartonVerifyPage = lazy(() => import('@/features/cartons/verify/CartonVerifyPage'));
// slice-907: final four carton-station rebuilds from the needs-rebuild backlog.
const CartonPickpackByStorePage = lazy(
  () => import('@/features/cartons/pickpack-by-store/CartonPickpackByStorePage'),
);
const CartonConfirmPage = lazy(() => import('@/features/cartons/confirm/CartonConfirmPage'));
const CartonRepackShipSioPage = lazy(
  () => import('@/features/cartons/repack-ship-sio/CartonRepackShipSioPage'),
);
const AsnCartonPackPage = lazy(
  () => import('@/features/cartons/asncartonpack/AsnCartonPackPage'),
);
// slice-910: order-fastpick family rebuilds from the needs-rebuild backlog.
const OrderFastpickPage = lazy(
  () => import('@/features/orders/fastpick/OrderFastpickPage'),
);
const OrderFastpickShipPage = lazy(
  () => import('@/features/orders/fastpick-ship/OrderFastpickShipPage'),
);
const OrderFastpickBuild1MPage = lazy(
  () => import('@/features/orders/fastpick-build-1-m/OrderFastpickBuild1MPage'),
);
const OrderFastPickTruckPage = lazy(
  () => import('@/features/orders/fastpicktruck/OrderFastPickTruckPage'),
);
// slice-911: kit-building family rebuilds from the needs-rebuild backlog.
const KitBuildingPage = lazy(
  () => import('@/features/kit/building/KitBuildingPage'),
);
const KitBuildingBatchBomPage = lazy(
  () => import('@/features/kit/building-batch-bom/KitBuildingBatchBomPage'),
);
// slice-923: truck carton load/unload station rebuilds from the needs-rebuild backlog.
const TruckLoadCartonToPage = lazy(
  () => import('@/features/trucks/load-carton-to/TruckLoadCartonToPage'),
);
const TruckUnloadCartonFromPage = lazy(
  () => import('@/features/trucks/unload-carton-from/TruckUnloadCartonFromPage'),
);
// slice-924: carton LTL consolidate/deconsolidate station rebuilds.
const CartonMovePage = lazy(
  () => import('@/features/cartons/move/CartonMovePage'),
);
const CartonMoveByOrdPage = lazy(
  () => import('@/features/cartons/move-by-ord/CartonMoveByOrdPage'),
);
const CartonVerifyWShipPage = lazy(
  () => import('@/features/cartons/verify-w-ship/CartonVerifyWShipPage'),
);
const PickedConsolidationPage = lazy(
  () => import('@/features/cartons/picked-consolidation/PickedConsolidationPage'),
);
const MultiSrcPickpackShipPage = lazy(
  () => import('@/features/cartons/multi-src-pickpack-ship/MultiSrcPickpackShipPage'),
);
const ShipVoidPage = lazy(() => import('@/features/shipping/void/ShipVoidPage'));
const RateShopOrderPage = lazy(
  () => import('@/features/shipping/rateshop/RateShopOrderPage'),
);
const ShipMiscPage = lazy(() => import('@/features/shipping/misc/ShipMiscPage'));
const ShipBatchPage = lazy(() => import('@/features/shipping/batch/ShipBatchPage'));
const ShipByCartonPage = lazy(
  () => import('@/features/shipping/by-carton/ShipByCartonPage'),
);
const ShipMultiCartonsPage = lazy(
  () => import('@/features/shipping/multi-cartons/ShipMultiCartonsPage'),
);
const ShipDayEndPage = lazy(() => import('@/features/shipping/dayend/ShipDayEndPage'));
// #527: w_ship_dayend_open (Day End for Open Manifests) is a DISTINCT PB window from
// w_ship_dayend — /ship/dayend was reusing the wrong page.
const ShipDayEndOpenPage = lazy(() => import('@/features/shipping/dayend/ShipDayEndOpenPage'));
// #849 (slice-1114): zoom-target pages built by parallel agents.
const ProductionRunPage = lazy(() => import('@/features/production/run/ProductionRunPage'));
// slice-3008 (#2092/#2093): w_production_run INSERT mode — "Production Run (New)".
const ProductionRunCreatePage = lazy(
  () => import('@/features/production/run/ProductionRunCreatePage'),
);
// slice-3008 (#2096): w_cart_assign_start — Start Pick Ticket Assignment.
const CartAssignStartPage = lazy(
  () => import('@/features/orders/cartassign/CartAssignStartPage'),
);
// slice-3111: w_cart_assign_finish — Finish Pick Ticket Assignment.
const CartAssignFinishPage = lazy(
  () => import('@/features/orders/cartassign/CartAssignFinishPage'),
);
const StyleStatusPage = lazy(() => import('@/features/style/status/StyleStatusPage'));
const TrailerMaintenancePage = lazy(() => import('@/features/yard/trailer/TrailerMaintenancePage'));
const TrailerSearchPage = lazy(() => import('@/features/yard/trailers/TrailerSearchPage'));
const KitRunPage = lazy(() => import('@/features/kit/run/KitRunPage'));
// slices 2673-2681: PB w_kit_run (insert) — kit-runs browse toolbar New.
const KitRunCreatePage = lazy(() => import('@/features/kit/run/KitRunCreatePage'));
const ItemCostByShipmentPage = lazy(() => import('@/features/items/cost/ItemCostByShipmentPage'));
const GroupsForUserPage = lazy(() => import('@/features/usergroup/groupsforuser/GroupsForUserPage'));
const InboundAsnDetailPage = lazy(() => import('@/features/receipts/inboundasn/InboundAsnDetailPage'));
const PreReceiptLineDetailPage = lazy(() => import('@/features/receipts/prereceipt/PreReceiptLineDetailPage'));
const AutoRestockPage = lazy(() => import('@/features/restock/autorestock/AutoRestockPage'));
const TruckLoadWavingPage = lazy(
  () => import('@/features/orders/truckloadwaving/TruckLoadWavingPage'),
);
const ShipReprocessErrorPage = lazy(
  () => import('@/features/shipping/reprocess-error/ShipReprocessErrorPage'),
);
const ShipStationPage = lazy(
  () => import('@/features/shipping/station/ShipStationPage'),
);
// Slice-778: Testers Hub -- UAT sign-off + issue tracking for every landable PH10 page.
const HardwareTestPage = lazy(() => import('@/features/hardwaretest/HardwareTestPage'));
const ReportPdfCapturePage = lazy(
  () => import('@/features/reports/pdfverify/ReportPdfCapturePage'),
);
const ReportPdfStatusPage = lazy(
  () => import('@/features/reports/pdfverify/ReportPdfStatusPage'),
);
const LoadScreenshotsPage = lazy(
  () => import('@/features/testershub/LoadScreenshotsPage'),
);
const TestersHubPage = lazy(
  () => import('@/features/testershub/TestersHubPage'),
);
const TestScreensPage = lazy(
  () => import('@/features/testershub/queue/TestScreensPage'),
);
// slice-3683 (CHRIS): the FUNCTIONAL round's own guided queue — serves the tester the screen
// they should be working on instead of letting them pick. Open to every authenticated user,
// exactly like /test-screens.
const FunctionalTestScreensPage = lazy(
  () => import('@/features/testershub/queue/FunctionalTestScreensPage'),
);
// slice-3930 (John 2026-10-06): TEMPORARY "My top screens" tester survey (backend-flag gated).
const MyTopScreensPage = lazy(
  () => import('@/features/testershub/topscreens/MyTopScreensPage'),
);
// slice-918 (CHRIS): "What's Happening" — rolling log of Claude's work,
// rendered from the static /whats-happening.html. Sits below Testers Hub.
// slice-3653 PROTOTYPE: functional-round test card pop-out window (hub-gated inside).
const TestCardPopoutPage = lazy(() => import('@/shared/testcard/TestCardPopoutPage'));
const FunctionalRoundPage = lazy(() => import('@/shared/testcard/FunctionalRoundPage'));
const WhatsHappeningPage = lazy(
  () => import('@/features/testershub/WhatsHappeningPage'),
);
// tester #653: PB w_pick_ticket_assign ("Simple Order Assignment") — single-order
// assignment FORM (not a grid). Static path shadows /browse/:key; the search screen drills
// in with the highlighted row's context as query params.
const SimpleOrderAssignPage = lazy(
  () => import('@/features/orders/simpleorderassign/SimpleOrderAssignPage'),
);

export function AppRouter() {
  return (
    <Suspense fallback={<div className="text-slate-500">Loading…</div>}>
      {/* slice-3690: the DEMO instance's "New Screens" slider. One wrapper, above the whole
          tree, because two of the seven swapped screens (/browse/order-tag-search,
          /browse/cfg-locations) have no route of their own -- they resolve through
          /browse/:key. Off-demo, or with the slider off, this renders <Routes> untouched. */}
      {/* ADR-0067 decision 6 (slice-4286): the ONE window guard for every route. Pass-through
          without a session (RequireAuth still owns the /login redirect); log mode always renders
          the page and only reports a would-403; enforce mode shows PB's refusal. */}
      <RequireWindow access={APP_SCREEN_ACCESS}>
      <NewScreensSwap>
      <Routes>
        {/* Slice-329 (feedback #4): root redirects to /home (landing page stub).
            Was /orders/search which dumped operators straight into the search grid
            with results. Full graphical dashboard slated for a follow-up slice. */}
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route
          path="/home"
          element={
            <RequireAuth>
              <HomePage />
            </RequireAuth>
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/system/ping" element={<PingPage />} />
        <Route
          path="/about"
          element={
            <RequireAuth>
              <AboutPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/search"
          element={
            <RequireAuth>
              <OrderMaintenancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/new"
          element={
            <RequireAuth>
              <OrderCreatePage />
            </RequireAuth>
          }
        />
        {/* /orders/maintenance (no params) is the legacy URL for the list page;
            redirect to the new canonical /orders/search. Detail routes below
            (with :orderId/:releaseNum) still resolve to the OrderInquiryPage. */}
        <Route path="/orders/maintenance" element={<Navigate to="/orders/search" replace />} />
        <Route
          path="/orders/inquiry/:orderId/:releaseNum"
          element={
            <RequireAuth>
              <OrderInquiryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/maintenance/:orderId/:releaseNum"
          element={
            <RequireAuth>
              <OrderInquiryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/maintenance/:orderId/:releaseNum/lines"
          element={
            <RequireAuth>
              <OrderLinesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/manage/work-pool"
          element={
            <RequireAuth>
              <WorkPoolPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/work-pool"
          element={
            <RequireAuth>
              <WorkPoolBrowseRedirect />
            </RequireAuth>
          }
        />
        <Route
          path="/manage/work-pool/users"
          element={
            <RequireAuth>
              <WorkPoolUserAssignedPage />
            </RequireAuth>
          }
        />
        {/* #2702 (KATHY, and the real root of #2658/#2704): w_work_usr_assign is the shared
            child of BOTH w_work_pool (ue_user_work_assigned) and w_work_search ("User Work
            Assigned" toolbar item, which navigates to /browse/usr-work-assign). slice-3296
            rebuilt the component but only the /manage/work-pool/users caller ever reached it —
            the work-search drill kept landing on the generic /browse/:key 4-column cfg
            (Whs/User/Assign Date/Time/Assign User; missing Tag, POP, COP, FOP/BOP, CRP/FRP,
            CYC, Other Work, Mover per dw_work_usr_assign.srd). Dedicated page shadows the
            generic /browse/:key so the tester's path serves the conformed grid (drill-target
            conformance: one shared child fixes all callers). Same URL, so hub issue collapse
            needs no ROUTE_ALIASES entry. */}
        <Route
          path="/browse/usr-work-assign"
          element={
            <RequireAuth>
              <WorkPoolUserAssignedPage />
            </RequireAuth>
          }
        />
        <Route
          path="/manage/work-pool/detail/:workSeqNum"
          element={
            <RequireAuth>
              <WorkPoolDetailPage />
            </RequireAuth>
          }
        />
        {/* #2481 (ANDREW): PB w_work_pool_wc, opened by "Ship &Labels Lookup" from BOTH
            w_work_pool (ue_cart, srw:539-574 — no work_det_seq_num) and
            w_work_pool_detail (srw:96-160 — with one). */}
        <Route
          path="/manage/work-pool/:workSeqNum/ship-labels"
          element={
            <RequireAuth>
              <WorkPoolShipLabelsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/manage/picks"
          element={
            <RequireAuth>
              <PicksPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/gen-receipt/:orderId/:releaseNum"
          element={
            <RequireAuth>
              <GenReceiptPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/split"
          element={
            <RequireAuth>
              <OrderSplitPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/split/:orderId/:releaseNum"
          element={
            <RequireAuth>
              <OrderSplitPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/gen-rma/:orderId/:releaseNum"
          element={
            <RequireAuth>
              <GenRmaPage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/maintenance"
          element={
            <RequireAuth>
              <ReceiptMaintenancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/maintenance/:po/:releaseNum"
          element={
            <RequireAuth>
              <ReceiptMaintenancePage />
            </RequireAuth>
          }
        />
        {/* /customers/maintenance(/:custId) retired (slice-3425, audit item #3): a superseded
            parallel port of the PB customer/ship-to window, unreachable from any nav entry,
            drill, or the testers-hub catalog (docs/audits/duplicate-ports/verdicts.json already
            recommended retiring it). The real, live, fully-wired window is
            CustomerShipToCreatePage at /customer-shipto/new + /customer-shiptos/:ownerId/:custId
            (browseConfigs.ts 'customer-shipto-search' drills there — #2511 already wired its
            New/Delete). */}
        <Route
          path="/items/inquiry"
          element={
            <RequireAuth>
              <ItemInquiryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/inquiry/:itemId"
          element={
            <RequireAuth>
              <ItemInquiryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/status"
          element={
            <RequireAuth>
              <ItemStatusPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/status/:ownerId/:itemId"
          element={
            <RequireAuth>
              <ItemStatusPage />
            </RequireAuth>
          }
        />
        {/* EXPERIMENT (Chris-only): /items/status2 — see ItemStatus2Page.tsx header. */}
        <Route
          path="/items/status2"
          element={
            <RequireAuth>
              <ItemStatus2Page />
            </RequireAuth>
          }
        />
        <Route
          path="/items/status2/:ownerId/:itemId"
          element={
            <RequireAuth>
              <ItemStatus2Page />
            </RequireAuth>
          }
        />
        {/* slice-1039 (#757): Item Search "Item Hold" zoom -> PB w_itemhold. */}
        <Route
          path="/items/hold/:ownerId/:itemId"
          element={
            <RequireAuth>
              <ItemHoldPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/:ownerId/:itemId/upc-codes"
          element={
            <RequireAuth>
              <UpcCodesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/:ownerId/:itemId/state-product-codes"
          element={
            <RequireAuth>
              <StateProductCodesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/:ownerId/:itemId/substitutions"
          element={
            <RequireAuth>
              <SubstitutionsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/:ownerId/:itemId/velocity-history"
          element={
            <RequireAuth>
              <VelocityHistoryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/location-types/:locationType/holds"
          element={
            <RequireAuth>
              <HoldsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/location-types/:locationType/inv-types"
          element={
            <RequireAuth>
              <InvTypesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/location-types/:locationType/rec-sort-viols"
          element={
            <RequireAuth>
              <RecSortViolsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/owners/:ownerId/carriers"
          element={
            <RequireAuth>
              <OwnerCarriersPage />
            </RequireAuth>
          }
        />
        <Route
          path="/owners/:ownerId/emails"
          element={
            <RequireAuth>
              <OwnerEmailsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/owners/:ownerId/counters"
          element={
            <RequireAuth>
              <OwnerCountersPage />
            </RequireAuth>
          }
        />
        {/* slice-836: PB w_warehouse_ctr — per-warehouse counters */}
        <Route
          path="/warehouses/:warehouseId/counters"
          element={
            <RequireAuth>
              <WarehouseCountersPage />
            </RequireAuth>
          }
        />
        <Route
          path="/warehouses/:warehouseId/label-cfg"
          element={
            <RequireAuth>
              <LabelCfgPage />
            </RequireAuth>
          }
        />
        <Route
          path="/carriers/:carrierId/cust-label-codes"
          element={
            <RequireAuth>
              <CustLblCodesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/carriers/:carrierId/qssi-label-codes"
          element={
            <RequireAuth>
              <QssiLblCodesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/carriers/:carrierId/services"
          element={
            <RequireAuth>
              <CarrierServicesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/customer-masters/:ownerId/:custCode/carriers"
          element={
            <RequireAuth>
              <CustomerCarriersPage />
            </RequireAuth>
          }
        />
        <Route
          path="/customer-masters/:ownerId/:custCode/not-ret-items"
          element={
            <RequireAuth>
              <NotRetItemsPage />
            </RequireAuth>
          }
        />
        {/* slice-3111: PB w_customer_master toolbar "Ship Carrier Ref PH Field"
            (wf_menu_new "shipcarrierreffieldph" srw:124) opens
            w_customer_master_ref_field_mapping keyed by owner_id/cust_code
            (ue_shipcarrierreffieldph srw:294-313). */}
        <Route
          path="/customer-masters/:ownerId/:custCode/ref-field-mapping"
          element={
            <RequireAuth>
              <CustRefFieldMappingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/customer-shiptos/:ownerId/:custId/comments"
          element={
            <RequireAuth>
              <ShipToCommentsPage />
            </RequireAuth>
          }
        />
        {/* slice-3093 (#2148): PB w_customer_shipto toolbar "&Not Returnable Item"
            (icon_items_not_returnable, w_customer_shipto.srw:341) opens
            w_customer_not_returnable_item keyed by owner_id/cust_id — the SHIP TO, not the
            Customer Master (that's the separate /customer-masters/.../not-ret-items route). */}
        <Route
          path="/customer-shiptos/:ownerId/:custId/not-ret-items"
          element={
            <RequireAuth>
              <ShipToNotRetItemsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/ship-methods/:shipMethod/cust-label-codes"
          element={
            <RequireAuth>
              <ShipMethodCustLblCodesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/ship-methods/:shipMethod/qssi-label-codes"
          element={
            <RequireAuth>
              <ShipMethodQssiLblCodesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/ship-methods/:shipMethod/error-codes"
          element={
            <RequireAuth>
              <ShipMethodErrorCodesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/action-log-type/:logType/codes"
          element={
            <RequireAuth>
              <LogCodesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/brand-codes/:brandCode/groups"
          element={
            <RequireAuth>
              <BrandGroupsPage />
            </RequireAuth>
          }
        />
        {/* slice-2925 (#1660): w_brand_group ue_zoom → w_brand_subgroup (the second
            zoom hop of the w_brand_code chain). */}
        <Route
          path="/brand-codes/:brandCode/groups/:groupCode/subgroups"
          element={
            <RequireAuth>
              <BrandSubgroupsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/:ownerId/:itemId/bom-types"
          element={
            <RequireAuth>
              <BomTypesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/change-log-types/:logType/codes"
          element={
            <RequireAuth>
              <ChangeLogCodesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/email-groups/:distCode/details"
          element={
            <RequireAuth>
              <EmailDistPage />
            </RequireAuth>
          }
        />
        <Route
          path="/harmonized-codes/:harmonizedCode/countries"
          element={
            <RequireAuth>
              <HarmonizedCountryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/load-plan-types/:loadPlanType/positions"
          element={
            <RequireAuth>
              <LoadPlanDetailPage />
            </RequireAuth>
          }
        />
        <Route
          path="/genrpt-categories/:categoryId/tables"
          element={
            <RequireAuth>
              <CategoryTablesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/notify-sets/:notifySet/instructions"
          element={
            <RequireAuth>
              <InstructionsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/load-plan-types/:loadPlanType/section-limits"
          element={
            <RequireAuth>
              <SectionLimitsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/pa-grps/:paGrp/classes"
          element={
            <RequireAuth>
              <PaGrpClassesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/users/:userId/favorites"
          element={
            <RequireAuth>
              <FavoritesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/order-assigns/new"
          element={
            <RequireAuth>
              <OrderAssignPage />
            </RequireAuth>
          }
        />
        <Route
          path="/order-assigns/:warehouseId/:ownerId/:orderId/:releaseNum"
          element={
            <RequireAuth>
              <OrderAssignPage />
            </RequireAuth>
          }
        />
        <Route
          path="/pick-grps/:pickGrp/classes"
          element={
            <RequireAuth>
              <PickGrpClassesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/rstk-grps/:rstkGrp/classes"
          element={
            <RequireAuth>
              <RstkGrpClassesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/carriers/:carrierId/error-codes"
          element={
            <RequireAuth>
              <ErrorCodesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/wave-types/:waveType/pick-rules"
          element={
            <RequireAuth>
              <WaveTypePickRulesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/warehouses/:warehouseId/emails"
          element={
            <RequireAuth>
              <WhsEmailsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/warehouses/:warehouseId/carton-types"
          element={
            <RequireAuth>
              <WhsCartonTypesPage />
            </RequireAuth>
          }
        />
        {/* #2537/#2538 (KATHY): PB w_warehouse_ship_method, the assigned-checkbox grid
            over the ship_method master. Replaces the /browse/warehouse-ship-method
            membership-table CRUD the w_warehouse toolbar used to open. */}
        <Route
          path="/warehouses/:warehouseId/ship-methods"
          element={
            <RequireAuth>
              <WarehouseShipMethodsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/production-lines/:warehouseId/:productionLine/source-locations"
          element={
            <RequireAuth>
              <SourceLocationsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/production-lines/:warehouseId/:productionLine/receive-locations"
          element={
            <RequireAuth>
              <ReceiveLocationsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/jobs/:warehouseId/:jobId/zones"
          element={
            <RequireAuth>
              <JobZonesPage />
            </RequireAuth>
          }
        />
        {/* PB w_productivity_standard — ue_prodstd opens it with string1=warehouse_id,
            string2=job_id (w_productivity_config.srw:80-83), so the two keys ride the URL
            exactly like the sibling w_job_zone zoom above. */}
        <Route
          path="/jobs/:warehouseId/:jobId/productivity-standard"
          element={
            <RequireAuth>
              <JobProductivityStandardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/wave-types/:waveType/put-stations"
          element={
            <RequireAuth>
              <PutStationsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/:ownerId/:itemId/bom"
          element={
            <RequireAuth>
              <BomPage />
            </RequireAuth>
          }
        />
        {/* slice-1040 (#757): PB w_item_bom_notes — BOM Notes drill from the BOM screen. */}
        <Route
          path="/items/:ownerId/:itemId/bom-notes"
          element={
            <RequireAuth>
              <BomNotesPage />
            </RequireAuth>
          }
        />
        {/* PB w_item_cfg_batch_clone: a Source Config form (dw_src: Owner / Item / Item
            Config Code / New Config Code / Set to Default) + Query By panel + tag-select
            result grid + Clone (fsp_clone_cfg_code per tagged row). The generic /browse/:key
            rendered only the result grid (Claudit score 2). Dedicated page served at
            /browse/item-cfg-batch-clone, shadowing the generic /browse/:key table browser. */}
        <Route
          path="/browse/item-cfg-batch-clone"
          element={
            <RequireAuth>
              <ItemCfgBatchClonePage />
            </RequireAuth>
          }
        />
        {/* campaign wave-4 (§4.22): PB w_waves — dedicated wave workbench (verbatim QBE +
            5 date ranges, 66-col editable grid w/ PB protects, REAL Manage Wave via
            wave_submit, Uncalc ladder, New/Save/Delete). */}
        <Route
          path="/browse/wave-status"
          element={
            <RequireAuth>
              <WaveStatusPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-4 (§4.22): PB w_global_receipt_maint — dedicated page (12-field
            QBE + 6 date pairs + tri-states, REAL tagged Delete via delete_receipt_recursive). */}
        <Route
          path="/browse/global-receipt-maint"
          element={
            <RequireAuth>
              <GlobalReceiptMaintPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-4 (§4.22): PB w_delete_open_orders — dedicated page (PB criteria
            + open-only predicates + REAL bulk delete: unsubmit/work-pool unwind +
            delete_order_recursive, per-row Msg). */}
        <Route
          path="/browse/delete-open-orders"
          element={
            <RequireAuth>
              <DeleteOpenOrdersPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-4 (§4.22): PB w_item_search — dedicated page (criteria pane w/
            owner-cascading dddws, (All)-owner + Suppress-UPC semantics, real guarded
            Delete, comment panel, profile bars). */}
        <Route
          path="/browse/items-search"
          element={
            <RequireAuth>
              <ItemSearchPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-4 (§4.22): PB w_bill_event_periodic — dedicated editable grid
            (protect rules + cascades, periodic bill codes, Clone SP, range drill). */}
        <Route
          path="/browse/bill-event-periodic"
          element={
            <RequireAuth>
              <BillEventPeriodicPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-4 (§4.22): PB w_auto_process_server_maint — dedicated page
            (Restart schedule form w/ cascades, real Reset, event_timer delete guard). */}
        <Route
          path="/browse/auto-process-server-maint"
          element={
            <RequireAuth>
              <AutoProcessServerMaintPage />
            </RequireAuth>
          }
        />
        {/* #1644 (ANDREW): PB w_auto_process_timer is the master-detail w_dwtsheet_dbl
            (dw_1 list + dw_2 detail FORM). Dedicated page shadows the generic /browse/:key
            grid; reuses the auto-process-timer generic-CRUD + reset endpoints. */}
        <Route
          path="/browse/auto-process-timer"
          element={
            <RequireAuth>
              <AutoProcessTimerPage />
            </RequireAuth>
          }
        />
        {/* batch-04 needs-rebuild: PB w_event_req_log is the master-detail w_dwtsheet_dbl
            (dw_1 list + dw_2 read-only detail FORM). Dedicated page shadows the generic
            /browse/:key grid; reuses the read-only event-log projection + tag/untag actions. */}
        <Route
          path="/browse/event-log"
          element={
            <RequireAuth>
              <EventLogPage />
            </RequireAuth>
          }
        />
        {/* slice-2972: PB w_price_change_review freeform record sheet (changed-tiers-only
            price lines). Dedicated read-only page shadows the generic /browse/:key grid;
            reuses the price-change-review TableDef read endpoint. */}
        <Route
          path="/browse/price-change-review"
          element={
            <RequireAuth>
              <PriceChangeReviewPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-5 (§4.22): PB w_bill_transaction_search — dedicated page (23-criteria
            Search By pane, union-select Description, cascades, Switch view; slice-1094 CRUD kept). */}
        <Route
          path="/browse/billing-transactions"
          element={
            <RequireAuth>
              <BillTransactionSearchPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-5 (§4.22): PB w_if_ul_order_status_search — dedicated page
            (15-field criteria + stat-date range, if_status inline-edit + real Save). */}
        <Route
          path="/browse/if-ul-order-status-search"
          element={
            <RequireAuth>
              <OrderStatusSearchPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-5 (§4.22): PB w_if_ul_trans_maintenance_search — dedicated page
            (26-field criteria pane, 5 PB-editable fields, partial-SET real Save). */}
        <Route
          path="/browse/if-ul-transaction-maintenance-search"
          element={
            <RequireAuth>
              <TransactionMaintenanceSearchPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-6 (§4.22): PB w_if_ul_ship_maintenance_search — dedicated page
            (16-field query pane + tag-delete of tagged if_ship_upl rows by PK). */}
        <Route
          path="/browse/if-ul-ship-maintenance-search"
          element={
            <RequireAuth>
              <ShipMaintenanceSearchPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-3 (§4.22): PB w_global_order_line_maint — dedicated editable
            order-lines page (cascades, per-row dddws, per-row-tx save + sp_order_calc). */}
        <Route
          path="/browse/global-order-line-maint"
          element={
            <RequireAuth>
              <GlobalOrderLineMaintPage />
            </RequireAuth>
          }
        />
        {/* PB w_order_container — reusable-container grid opened from w_orderstat's
            Containers action (license-gated, f_license_allow_reusablecontainer). Keys
            arrive as query params from the orders-status drill. */}
        <Route
          path="/orders/containers"
          element={
            <RequireAuth>
              <OrderContainersPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-3 (§4.22): PB w_bill_event_activity — dedicated 46-col editable
            grid w/ PB edit styles + cascades, one-tx save, Clone SP, loc-type panels. */}
        <Route
          path="/browse/bill-event-activity"
          element={
            <RequireAuth>
              <BillEventActivityPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-3 (§4.22): PB w_postdeduct — dedicated 2-tab (01/02) page with
            real CRUD (active) / Purge (history). Shadows the generic routes; the four
            post-deduct-* cfgs are removed. */}
        <Route
          path="/browse/post-deduct-01"
          element={
            <RequireAuth>
              <PostDeductPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/post-deduct-hist-01"
          element={
            <RequireAuth>
              <PostDeductHistoryPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-3 (§4.22): PB w_work_search — dedicated page (full query pane,
            tag grid, real delete-tagged/conveyor/group-modify, lotable panel). */}
        <Route
          path="/browse/work-search"
          element={
            <RequireAuth>
              <WorkSearchPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-3 (§4.22): PB w_label_view — dedicated dw_label_view3 card page
            (real criteria cascade + View Code; read-only per PB production). Shadows
            /browse/:key; old cfg + TableDef removed. */}
        {/* #1872 (CHRIS, audit-approved): PB w_location is a single-record FORM opened as
            "New Location" (ibparm_new -> insertrow) — the AG-grid at /browse/location was
            never PB. Redirect to the create form; issues filed there roll up via the
            RouteNormalizer alias + the slice-2885 group-aware queue guard. */}
        <Route path="/browse/location" element={<Navigate to="/location/new" replace />} />
        <Route
          path="/browse/label-view"
          element={
            <RequireAuth>
              <LabelViewPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-2 (§4.22): PB w_truckinbound_search — dedicated inbound-trucks
            workstation (C/H swap, editable Full View + cascades, Back/Forw dock machine,
            Manage/New/Save on real procs). Shadows /browse/:key. */}
        <Route
          path="/browse/inbound-trucks"
          element={
            <RequireAuth>
              <InboundTruckSearchPage />
            </RequireAuth>
          }
        />
        {/* campaign wave-1 (§4.22): PB w_labelgen is a label-profile editor + ZPL/fixeddata
            generator, not a browse. Dedicated page shadows /browse/:key; the old generic
            cfg + TableDef are superseded (removed at wave-end integration). */}
        <Route
          path="/browse/labelgen"
          element={
            <RequireAuth>
              <LabelGenPage />
            </RequireAuth>
          }
        />
        {/* #2514 / needs-chris card 27 — RETIRED 2026-08-05 (Chris: "full PB fidelity, so
            option b"). /browse/item-master rendered ItemMasterSearchPage, a PH10-INVENTED
            item-master search grid with no PB counterpart: w_item_master is opened by one
            menu leaf only (m_newitem "&New Item", m_main.srm:32877) and both of its open
            modes are SINGLE-RECORD (w_item_master.srw:893-896 insert / :902 one-row
            retrieve). PB's item search is a different window, w_item_search
            (m_main.srm:17005), already ported at /browse/items-search — which is where
            anyone arriving at this URL wanted to go, so that is the redirect target.
            Nothing lands here by design any more (no sidebar leaf, no drill, no catalog
            row); the redirect only catches stale bookmarks and old hub links. The page's
            two unique behaviours were already re-homed onto their real PB windows: the
            22-drill wf_menu_new toolbar onto ItemMasterPage (slice-3153/3165, both modes)
            and the #2276 Item-Status double-click onto ItemSearchPage (the w_item_search
            source it was ported from, srw:155). ROUTE_ALIASES still rolls the 18 historical
            issues up to /items/master, the catalog row the menu actually opens. */}
        <Route
          path="/browse/item-master"
          element={<Navigate to="/browse/items-search" replace />}
        />
        {/* #1473/#1474: PB w_domain_detail — 3-pane zoom (From/To location-type tag lists +
            checked-pair grid with Intermediate Staging Area). Served at /browse/domain-detail
            (where the w_domain Zoom drill + testers-hub link), shadowing the generic
            /browse/:key table browser. */}
        <Route
          path="/browse/domain-detail"
          element={
            <RequireAuth>
              <DomainDetailPage />
            </RequireAuth>
          }
        />
        {/* #1478: PB w_mask_detail is a freeform FORM (Line + flag boxes above the
            Validation / Display / Actions groupboxes), not a grid. Served at
            /browse/mask-detail (where the w_mask Zoom drill + testers-hub link),
            shadowing the generic /browse/:key table browser. */}
        <Route
          path="/browse/mask-detail"
          element={
            <RequireAuth>
              <MaskDetailPage />
            </RequireAuth>
          }
        />
        {/* slice-1077: PB w_cubiscan dimensioning workstation. Served at /browse/cubiscan
            (where nav.ts/testers-hub link), shadowing the generic /browse/:key. */}
        <Route
          path="/browse/cubiscan"
          element={
            <RequireAuth>
              <CubiscanPage />
            </RequireAuth>
          }
        />
        {/* slice-1081: PB w_ephs_jsps tree+grid+test-pane config editor. Served at
            /browse/ephs-jsps (where nav.ts/testers-hub link), shadowing the generic
            /browse/:key table browser. */}
        <Route
          path="/browse/ephs-jsps"
          element={
            <RequireAuth>
              <EphsJspsPage />
            </RequireAuth>
          }
        />
        {/* slice-1107: PB w_interface_services is a freeform single-record maintenance
            FORM (grouped rectangle panels), not a grid — the generic /browse/:key list
            was the wrong control class (Claudit score 2). Dedicated form page served at
            /browse/interface-services, shadowing the generic /browse/:key table browser. */}
        <Route
          path="/browse/interface-services"
          element={
            <RequireAuth>
              <InterfaceServicesPage />
            </RequireAuth>
          }
        />
        {/* PB w_mover_order_request is a w_dwtsheet_dbl: an auto-retrieved GRID
            (dw_mover_order_request2, the view PB switches to at open) toggling with a
            FREEFORM per-row detail form (dw_mover_order_request1, 22 fields). Dedicated
            page (grid default + qty editing + Save/Delete per #1440,
            docs/design/mover-order-request-rebuild.md), shadowing the generic
            /browse/:key table browser. */}
        <Route
          path="/browse/mover-order-request"
          element={
            <RequireAuth>
              <MoverOrderRequestPage />
            </RequireAuth>
          }
        />
        {/* PB w_season_assign (Seasonal Velocity Update) is a date-driven WORKSHEET —
            a "Date:" pane driving the retrieve, a dddw+auto-fill grid, and a Save that
            broadcasts velocities onto item_master.current_velocity (NOT row CRUD; the
            generic genericCrud binding wrote season_class master rows — #1440's sibling
            #1431, docs/design/season-assign-rebuild.md). Dedicated page shadowing the
            generic /browse/:key table browser. */}
        <Route
          path="/browse/season-assign"
          element={
            <RequireAuth>
              <SeasonAssignPage />
            </RequireAuth>
          }
        />
        {/* PB w_wave_type / dw_wave_type is a FREEFORM single-record maintenance
            FORM (~100 grouped fields: identity row + "Group On" role matrix + a
            3-column flag/dropdown/text body), NOT a grid — the generic /browse/:key
            list was the wrong control class (Claudit score 2). Dedicated form page
            served at /browse/wave-type, shadowing the generic /browse/:key table
            browser. Load/Save/Delete use the existing wave-type genericCrud endpoints. */}
        <Route
          path="/browse/wave-type"
          element={
            <RequireAuth>
              <WaveTypeMaintPage />
            </RequireAuth>
          }
        />
        {/* PB w_size_type_maint is a MASTER-DETAIL sheet: top grid (Size Type, X/Y
            Description, One/Two RADIO Dimensions) PLUS two Size/Sort detail sub-grids
            (dw_x seq_num 1 / dw_y seq_num 2) that load for the selected master row.
            The generic /browse/:key flat list could render none of that (Claudit #1176
            score 3, RUSS agreed). This dedicated page restores the radio + both detail
            panels; master writes reuse the size-type-maint genericCrud endpoints, detail
            writes use the items/sizetype slice. Shadows the generic /browse/:key. */}
        <Route
          path="/browse/size-type-maint"
          element={
            <RequireAuth>
              <SizeTypeMaintPage />
            </RequireAuth>
          }
        />
        {/* PB w_item_configuration_profile is a w_dwtsheet_dbl sheet: a record selector over
            item_cfg_profile PLUS a labeled single-record DETAIL FORM (Profile/Description/Cfg
            Code, Cases Per Pallet/Tier, Pieces Per Case/Inner, Floating Pick Bin Min/Max, Stack
            Factor, and a Pallet/Case/Inner/Piece × Weight/Height/Width/Depth/Cube dimension
            matrix). The generic /browse/:key flat grid hid the matrix behind the column chooser
            (Claudit #1175 score 3, RUSS agreed). This dedicated page restores PB's form paradigm
            with the dimension matrix; writes reuse the item-configuration-profile genericCrud
            endpoints (single flat table, no child rows). Shadows the generic /browse/:key. */}
        <Route
          path="/browse/item-configuration-profile"
          element={
            <RequireAuth>
              <ItemConfigProfilePage />
            </RequireAuth>
          }
        />
        {/* slice-1090 (ADR-0013): appointment board (MVP) shadows the 3 generic /browse/appoint-* grids. */}
        <Route
          path="/browse/appoint-prod-line"
          element={<RequireAuth><AppointmentBoardPage screenKey="appoint-prod-line" /></RequireAuth>}
        />
        <Route
          path="/browse/appoint-dock"
          element={<RequireAuth><AppointmentBoardPage screenKey="appoint-dock" /></RequireAuth>}
        />
        <Route
          path="/browse/appoint-kit"
          element={<RequireAuth><AppointmentBoardPage screenKey="appoint-kit" /></RequireAuth>}
        />
        {/* w_report_image: dedicated page (grid + Load Image / Show Image blob actions);
            shadows the generic /browse/:key table browser. */}
        <Route
          path="/browse/report-image"
          element={
            <RequireAuth>
              <ReportImagePage />
            </RequireAuth>
          }
        />
        {/* w_rf_modules: dedicated full-CRUD maintenance grid (+ RF Mover picker,
            change-log audit); shadows the generic /browse/:key table browser. */}
        <Route
          path="/browse/rf-modules"
          element={
            <RequireAuth>
              <RfModulesPage />
            </RequireAuth>
          }
        />
        {/* w_routing_order_tag: dedicated tag→route page (filter bar + multi-select grid
            + Submit for Routing + Comments/Order Lines panels); shadows /browse/:key. */}
        <Route
          path="/browse/routing-order-tag"
          element={
            <RequireAuth>
              <RoutingOrderTagPage />
            </RequireAuth>
          }
        />
        {/* w_prod_lot_assign (#913/#914): dedicated 4-section lot-assignment workflow
            (header + existing-components grid + editable assign grid + Save); shadows /browse/:key. */}
        <Route
          path="/browse/prod-lot-assign"
          element={
            <RequireAuth>
              <ProdLotAssignPage />
            </RequireAuth>
          }
        />
        {/* w_label_mapping: config/developer tool over lbl_mapping — selection panel,
            developer-mode gate, Generate Fixeddata / Test Expressions; shadows /browse/:key. */}
        <Route
          path="/browse/label-mapping"
          element={
            <RequireAuth>
              <LabelMappingPage />
            </RequireAuth>
          }
        />
        {/* w_pre_pack_item_build: header form + component BOM grid + Build workflow;
            shadows the generic /browse/:key table browser. */}
        <Route
          path="/browse/pre-pack-item-build"
          element={
            <RequireAuth>
              <PrePackItemBuildPage />
            </RequireAuth>
          }
        />
        {/* w_translate_maint: Window Translation (crt_translate family) — language +
            DataWindow header over an editable field grid; shadows /browse/:key. */}
        <Route
          path="/browse/cfg-translate"
          element={
            <RequireAuth>
              <WindowTranslationPage />
            </RequireAuth>
          }
        />
        {/* w_if_download_ship_search: Download Ship Maintenance Search — criteria + tag-grid
            + if_status write-back; shadows /browse/:key. */}
        <Route
          path="/browse/if-download-ship-search"
          element={
            <RequireAuth>
              <ShipDownloadSearchPage />
            </RequireAuth>
          }
        />
        {/* w_rcv_pa (#927/#928): Receipt Manual Putaway — query→cascade→grid→putaway-commit
            over the platform move services; shadows /browse/:key. */}
        <Route
          path="/browse/rcv-pa"
          element={
            <RequireAuth>
              <ManualPutawayPage />
            </RequireAuth>
          }
        />
        {/* w_truck_schedule_manager (#907/#908): dual-panel drag-drop scheduling board;
            shadows the generic /browse/:key table browser. */}
        <Route
          path="/browse/truck-schedule"
          element={
            <RequireAuth>
              <TruckScheduleBoardPage />
            </RequireAuth>
          }
        />
        {/* w_usrs: Users maintenance form (drilled from the cfg-users browse).
            /new must precede /:usrId. */}
        <Route
          path="/users/maintenance/new"
          element={
            <RequireAuth>
              <UserMaintenancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/users/maintenance/:usrId"
          element={
            <RequireAuth>
              <UserMaintenancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/owners/:ownerId/ship-methods"
          element={
            <RequireAuth>
              <OwnerShipMethodsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/change-log-groups/:grpId/log-types"
          element={
            <RequireAuth>
              <GroupLogTypesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/user-groups/:grpId/users"
          element={
            <RequireAuth>
              <GroupUsersPage />
            </RequireAuth>
          }
        />
        <Route
          path="/modules/:moduleId/groups"
          element={
            <RequireAuth>
              <ModuleGroupsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/:ownerId/:itemId/inv-types"
          element={
            <RequireAuth>
              <ItemInvTypesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/:ownerId/:itemId/equipment"
          element={
            <RequireAuth>
              <ItemEquipmentPage />
            </RequireAuth>
          }
        />
        <Route
          path="/customer-shiptos/:ownerId/:custId/carriers"
          element={
            <RequireAuth>
              <CustomerShiptoCarriersPage />
            </RequireAuth>
          }
        />
        <Route
          path="/owners/:ownerId/ref-field-mapping"
          element={
            <RequireAuth>
              <RefFieldMappingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/languages/:languageId/translations"
          element={
            <RequireAuth>
              <TranslationsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/column-translations"
          element={
            <RequireAuth>
              <ColumnTranslatePage />
            </RequireAuth>
          }
        />
        {/* slice-1556 (Chris direct): w_activity_user_group_det — the real PB zoom child
            (user-membership tag grid) for /browse/activity-user-group, replacing the
            PendingPortModal stub. */}
        <Route
          path="/config/activity-user-group/:profileId"
          element={
            <RequireAuth>
              <ActivityUserGroupDetailPage />
            </RequireAuth>
          }
        />
        {/* ADR-0020 zoom sweep #12: w_import_pcl_dim_wgt_charges — the real PB zoom target
            (400-column parcel weight-break matrix + file import + destructive resave) for
            /browse/rate-code, replacing the pbSubWindow "not yet ported" stub. Opened with
            ?rateCode=… (PB gstr_winparm.string1). */}
        <Route
          path="/shipping/pcl-charges"
          element={
            <RequireAuth>
              <PclChargesPage />
            </RequireAuth>
          }
        />
        {/* w_translate_column (cfg-ctranslate1 rebuild): the language-driven side-by-side
            column-translation editor also shadows the old /browse/cfg-ctranslate1 route
            (same static-shadow pattern as cfg-mtranslate1 below). The invented 3-tab
            generic decomposition was removed from browseConfigs/TableBrowserRegistry. */}
        <Route
          path="/browse/cfg-ctranslate1"
          element={
            <RequireAuth>
              <ColumnTranslatePage />
            </RequireAuth>
          }
        />
        {/* w_rules steps editors (cfg-pa-rule rebuild): PB ue_zoom targets w_putprio /
            w_pickprio / w_restprio; Clone Rule = PB w_clone (rule-*). */}
        <Route
          path="/config/rules/:type/:rule"
          element={
            <RequireAuth>
              <RuleStepsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/rules/:type/:rule/clone"
          element={
            <RequireAuth>
              <CloneRulePage />
            </RequireAuth>
          }
        />
        {/* w_translate_msg: Message Translation editor (crt_mtranslate family); shadows
            the generic /browse/:key 3-tab browser. */}
        <Route
          path="/browse/cfg-mtranslate1"
          element={
            <RequireAuth>
              <MessageTranslationPage />
            </RequireAuth>
          }
        />
        {/* w_translate_rf_msg (#1019): RF Message Translation editor (rf_jmessages family);
            language-driven record cards, shadows the generic /browse/:key browser. */}
        <Route
          path="/browse/translate-rf-msg"
          element={
            <RequireAuth>
              <RfMessageTranslationPage />
            </RequireAuth>
          }
        />
        {/* w_adjust_search (#1020): Adjust Inventory Search — Query-By criteria pane +
            Search-For radio over the results grid (ADR-0010); shadows the generic browser. */}
        <Route
          path="/browse/adjust-search"
          element={
            <RequireAuth>
              <AdjustSearchPage />
            </RequireAuth>
          }
        />
        <Route
          path="/field-formats/:fieldFormat/details"
          element={
            <RequireAuth>
              <FormatDetailsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/carriers/:carrierId/address-mapping"
          element={
            <RequireAuth>
              <AddressMappingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/zones/:warehouseId/:zoneId/locations"
          element={
            <RequireAuth>
              <ZoneLocationsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/zones/:warehouseId/:zoneId/users"
          element={
            <RequireAuth>
              <ZoneUsersPage />
            </RequireAuth>
          }
        />
        <Route
          path="/zones/:warehouseId/:zoneId/devices"
          element={
            <RequireAuth>
              <ZoneDevicesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/owners/:ownerId/auto-hold-sets"
          element={
            <RequireAuth>
              <AutoHoldSetsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/change-log-types/:logType/more-cols"
          element={
            <RequireAuth>
              <MoreColsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/:ownerId/:itemId/configurations"
          element={
            <RequireAuth>
              <ItemConfigurationsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/rf-messages/:messageId/:messageSource/translations"
          element={
            <RequireAuth>
              <RfMessagesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/divisions/:warehouseId/:ownerId/:divisionId/carriers"
          element={
            <RequireAuth>
              <DivisionCarriersPage />
            </RequireAuth>
          }
        />
        {/* #70/#71 (slice-1145): Labor Task Search rebuild of w_labor_task_search — dedicated
            search page (Current/History + date ranges). Old /browse/prod-labor-tasks redirects
            here; rows zoom into the existing /labor-tasks/:warehouseId/:laborSeq detail. */}
        <Route
          path="/labor-tasks"
          element={
            <RequireAuth>
              <LaborTaskSearchPage />
            </RequireAuth>
          }
        />
        <Route path="/browse/prod-labor-tasks" element={<Navigate to="/labor-tasks" replace />} />
        {/* flag (av): labor-task INSERT — PB w_labor_task_search ue_insertrow → w_labor_task("insert"). */}
        <Route
          path="/labor-tasks/new"
          element={
            <RequireAuth>
              <LaborTaskCreatePage />
            </RequireAuth>
          }
        />
        <Route
          path="/labor-tasks/:warehouseId/:laborSeq"
          element={
            <RequireAuth>
              <LaborTasksPage />
            </RequireAuth>
          }
        />
        <Route
          path="/labor-tasks/:warehouseId/:laborSeq/users"
          element={
            <RequireAuth>
              <LaborUsersPage />
            </RequireAuth>
          }
        />
        <Route
          path="/kit-runs/:warehouseId/:kitRun/requests"
          element={
            <RequireAuth>
              <KitRequestsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/aql-rules/:aqlRule/details"
          element={
            <RequireAuth>
              <AqlDetailsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/carton-types/:cartonType/packaging"
          element={
            <RequireAuth>
              <PackagingTypesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/master"
          element={
            <RequireAuth>
              <ItemMasterPage />
            </RequireAuth>
          }
        />
        <Route
          path="/items/master/:ownerId/:itemId"
          element={
            <RequireAuth>
              <ItemMasterPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/brand-codes"
          element={
            <RequireAuth>
              <BrandCodePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/brand-codes/:brandCode"
          element={
            <RequireAuth>
              <BrandCodePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/brand-groups"
          element={
            <RequireAuth>
              <BrandGroupPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/brand-groups/:brandCode/:groupCode"
          element={
            <RequireAuth>
              <BrandGroupPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/brand-subgroups"
          element={
            <RequireAuth>
              <BrandSubgroupPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/brand-subgroups/:brandCode/:groupCode/:subgroupCode"
          element={
            <RequireAuth>
              <BrandSubgroupPage />
            </RequireAuth>
          }
        />
        {/* slice-814: PB w_qc_rate (single window, 4 tabs) */}
        <Route
          path="/config/qc-rates"
          element={
            <RequireAuth>
              <TabbedQcRatePage />
            </RequireAuth>
          }
        />
        {/* slice-3626 (ADR-0050): demo-instance-only Slingshot screens — RequireDemoSystem renders
            "Not found." when the instance flag is off (backend DemoSystemGate 404s the APIs). */}
        <Route
          path="/reports/floor-pulse"
          element={
            <RequireAuth>
              <RequireDemoSystem>
                <FloorPulsePage />
              </RequireDemoSystem>
            </RequireAuth>
          }
        />
        <Route
          path="/reports/labor-by-user"
          element={
            <RequireAuth>
              <RequireDemoSystem>
                <LaborByUserPage />
              </RequireDemoSystem>
            </RequireAuth>
          }
        />
        <Route
          path="/reports/labor-uph"
          element={
            <RequireAuth>
              <RequireDemoSystem>
                <LaborUphPage />
              </RequireDemoSystem>
            </RequireAuth>
          }
        />
        <Route
          path="/reports/monthly-kpi"
          element={
            <RequireAuth>
              <RequireDemoSystem>
                <MonthlyKpiPage />
              </RequireDemoSystem>
            </RequireAuth>
          }
        />
        <Route
          path="/reports/shift-recap"
          element={
            <RequireAuth>
              <RequireDemoSystem>
                <ShiftRecapPage />
              </RequireDemoSystem>
            </RequireAuth>
          }
        />
        <Route
          path="/reports/user-scorecard"
          element={
            <RequireAuth>
              <RequireDemoSystem>
                <UserScorecardPage />
              </RequireDemoSystem>
            </RequireAuth>
          }
        />
        <Route
          path="/reports/user-scorecard/:userId"
          element={
            <RequireAuth>
              <RequireDemoSystem>
                <UserScorecardPage />
              </RequireDemoSystem>
            </RequireAuth>
          }
        />
        <Route
          path="/reports/user-travel"
          element={
            <RequireAuth>
              <RequireDemoSystem>
                <UserTravelPage />
              </RequireDemoSystem>
            </RequireAuth>
          }
        />
        <Route
          path="/reports/labor-task-assign"
          element={
            <RequireAuth>
              <RequireDemoSystem>
                <LaborTaskAssignmentPage />
              </RequireDemoSystem>
            </RequireAuth>
          }
        />
        <Route
          path="/reports/work-visualization"
          element={
            <RequireAuth>
              <RequireDemoSystem>
                <WorkVisualizationPage />
              </RequireDemoSystem>
            </RequireAuth>
          }
        />
        <Route
          path="/manage-warehouse/warehouse-status"
          element={
            <RequireAuth>
              <RequireDemoSystem>
                <WarehouseStatusPage />
              </RequireDemoSystem>
            </RequireAuth>
          }
        />
        <Route
          path="/tools/rf-simulator"
          element={
            <RequireAuth>
              <RequireRfSimulator>
                <RfSimulatorPage />
              </RequireRfSimulator>
            </RequireAuth>
          }
        />
        {/* slice-815: PB w_shippingtables (single window, 19 tabs) */}
        <Route
          path="/config/shipping-tables"
          element={
            <RequireAuth>
              <TabbedShippingTablesPage />
            </RequireAuth>
          }
        />
        {/* slice-3351 (#2878/#2929 KATHY): the two special-services Zoom children of
            w_shippingtables, rebuilt as dedicated pages (per-row field_type edit variants
            + the ship-via delete-all/insert save PB runs in ue_update — neither expressible
            on the generic browse). Static paths shadow the generic /browse/:key, so the
            existing zoom drills and hub issue collapse keep the SAME URLs (usr-work-assign
            precedent, #2702). */}
        <Route
          path="/browse/special-srvc-intcode"
          element={
            <RequireAuth>
              <SpecialServicesForServicePage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/special-srvc-intcode-shipvia"
          element={
            <RequireAuth>
              <SpecialServicesForShipViaPage />
            </RequireAuth>
          }
        />
        {/* slice-816: PB w_production_status (single window, 6 inquiry tabs) */}
        <Route
          path="/inquiry/production-status"
          element={
            <RequireAuth>
              <TabbedProductionStatusPage />
            </RequireAuth>
          }
        />
        {/* slice-817: PB w_report_types (single window, 3 tabs partitioning report_type) */}
        <Route
          path="/config/report-types"
          element={
            <RequireAuth>
              <TabbedReportTypesPage />
            </RequireAuth>
          }
        />
        {/* slice-818: PB w_item_master_default (single window, 5 tabs on one item_master row) */}
        <Route
          path="/items/default-maintenance/:ownerId/:itemId"
          element={
            <RequireAuth>
              <TabbedItemMasterDefaultPage />
            </RequireAuth>
          }
        />
        {/* slice-819: PB w_qc_check (QC Inspect) — full operator workflow */}
        <Route
          path="/qc/inspect"
          element={
            <RequireAuth>
              <QcInspectPage />
            </RequireAuth>
          }
        />
        {/* slice-3925 (#3736): PB w_qc_check_truck — opened by the QC Inspect icon (of_dw_qc) */}
        <Route
          path="/qc/inspect-truck"
          element={
            <RequireAuth>
              <TruckQcInspectPage />
            </RequireAuth>
          }
        />
        {/* slice-820: PB w_alarms (single window, 2 tabs partitioning alarm.aggregate_flag) */}
        <Route
          path="/inquiry/alarms"
          element={
            <RequireAuth>
              <TabbedAlarmsPage />
            </RequireAuth>
          }
        />
        {/* slice-821: PB w_reasons (single window, 7 tabs over different reason_* tables) */}
        <Route
          path="/config/reasons"
          element={
            <RequireAuth>
              <TabbedReasonsPage />
            </RequireAuth>
          }
        />
        {/* slice-822: PB w_lock (single window, 8 tabs over locks + 4 related tables) */}
        <Route
          path="/manage/system-locks"
          element={
            <RequireAuth>
              <TabbedLockPage />
            </RequireAuth>
          }
        />
        {/* slice-823: PB w_container_type (single window, 6 tabs over container-type tables) */}
        <Route
          path="/config/container-types"
          element={
            <RequireAuth>
              <TabbedContainerTypePage />
            </RequireAuth>
          }
        />
        {/* slice-898: full-field pallet-type / carton-type edit pages (issue #359).
            Pallet/Carton rows link here so operators can edit dim/UOM/empty/carrier fields
            and Calculate Empty Cube (PB wf_update_empty_cube). */}
        <Route
          path="/config/container-types/pallet/:palletType/edit"
          element={
            <RequireAuth>
              <PalletTypeEditPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/container-types/carton/:cartonType/edit"
          element={
            <RequireAuth>
              <CartonTypeEditPage />
            </RequireAuth>
          }
        />
        {/* slice-824: PB w_route_detail (single window, 4 tabs over route-detail tables) */}
        <Route
          path="/config/route-detail"
          element={
            <RequireAuth>
              <TabbedRouteDetailPage />
            </RequireAuth>
          }
        />
        {/* slice-1741 (#1525): PB "New Item Class" = w_item_class_detail in NEW mode
            (w_item_class::ue_insertrow → boolean1='Y'). Same window/component, create mode. */}
        <Route
          path="/config/item-class-detail/new"
          element={
            <RequireAuth>
              <TabbedItemClassDetailPage mode="create" />
            </RequireAuth>
          }
        />
        {/* slice-825: PB w_item_class_detail (single window, 5 tabs on one item_class row) */}
        <Route
          path="/config/item-class-detail/:ownerId/:itemClass"
          element={
            <RequireAuth>
              <TabbedItemClassDetailPage />
            </RequireAuth>
          }
        />
        {/* PB w_item_class_d_cfg ("Default Configuration for Item Class") — opened from
            w_item_class_detail's Default Configurations toolbar item (ue_zoom_d_cfg:
            string1=owner_id, string2=item_class) and the w_item_class browse drill. */}
        <Route
          path="/config/item-class-default-cfg/:ownerId/:itemClass"
          element={
            <RequireAuth>
              <ItemClassDefaultCfgPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/user-group-detail/:grpId"
          element={
            <RequireAuth>
              <TabbedUserGroupDetailPage />
            </RequireAuth>
          }
        />
        {/* slice-828: PB w_hi_orddata (single window, 5 inquiry tabs keyed by order) */}
        <Route
          path="/orders/shipped-history/:ownerId/:orderId/:releaseNum"
          element={
            <RequireAuth>
              <TabbedShippedHistoryPage />
            </RequireAuth>
          }
        />
        {/* slice-829: PB w_truck_status_detail (7 inquiry tabs keyed by truck) */}
        <Route
          path="/shipping/truck-status-detail/:warehouseId/:truckId"
          element={
            <RequireAuth>
              <TabbedTruckStatusDetailPage />
            </RequireAuth>
          }
        />
        {/* slice-3126: PB w_trk — outbound Truck Maintenance full form (header + 5 tabs).
            Callers: Order Status dashboard "Truck Maintenance" (was a /browse/trucks
            mis-drill) + TruckDispatchPage 'maint' (was a placeholder). ?source=H = History. */}
        <Route
          path="/truck/maintenance/:warehouseId/:truckId"
          element={
            <RequireAuth>
              <TruckMaintenancePage />
            </RequireAuth>
          }
        />
        {/* #2414 (ANDREW): PB w_truckinbound — Inbound Truck Maintenance. Caller:
            truck-inout-search Zoom on an i-trk row (w_truck_inout_search.srw:153); the
            page's own Zoom drills on to /browse/truckinbound-detail (w_truckinbound_detail).
            ?source=H = History (hi_truckinbound_header, read-only). */}
        <Route
          path="/truck/inbound-maintenance/:warehouseId/:truckId"
          element={
            <RequireAuth>
              <InboundTruckMaintenancePage />
            </RequireAuth>
          }
        />
        {/* slice-830: PB w_wave_det (7 inquiry tabs keyed by wave) */}
        <Route
          path="/orders/wave-detail/:warehouseId/:waveSeqNum"
          element={
            <RequireAuth>
              <TabbedWaveDetailPage />
            </RequireAuth>
          }
        />
        {/* slice-3097: PB w_wave_load_plan drag-drop board, keyed by warehouse + wave (the same
            drill contract the interim wave-load-plan-detail grid used). */}
        <Route
          path="/orders/wave-load-plan/:warehouseId/:waveSeqNum"
          element={
            <RequireAuth>
              <LoadPlanBoardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/inv-types"
          element={
            <RequireAuth>
              <InvTypePage />
            </RequireAuth>
          }
        />
        {/* 2026-07-15 (CHRIS): removed /config/inv-types/:invType — PB w_inv_type is an
            inline-editable w_dwtsheet grid with no detail window; the grid at
            /config/inv-types edits in place. */}
        {/* slice-1060 (CHRIS): the no-param standalone entry duplicated the canonical
            grid /browse/item-tax-class (which drills into the :taxClass editor below).
            Redirect it to canonical so there's one Tax Classes screen + no second hub row.
            The :taxClass editor route stays — the grid drills into it. */}
        <Route
          path="/config/tax-classes"
          element={<Navigate to="/browse/item-tax-class" replace />}
        />
        <Route
          path="/config/tax-classes/:taxClass"
          element={
            <RequireAuth>
              <TaxClassPage />
            </RequireAuth>
          }
        />
        {/* Help finding I-062: PB w_hazmat_code is the grid + Switch View freeform only; the
            per-code /config/hazmat-codes/:hazmatCode page (HazmatPage) is retired. */}
        <Route
          path="/config/hazmat-codes"
          element={
            <RequireAuth>
              <TableBrowserPage cfgKey="hazmat-code" />
            </RequireAuth>
          }
        />
        {/* slice-3007 (#2098 CHRIS): PB w_zone is a FREEFORM tabular DataWindow
            (dw_zone_master, processing=0) — grouped two-tier headers ("Printer" over
            Report/Label, "Exclude Rating On" over COP/POP/BOP) over editable band rows,
            not an AG-grid. ZoneMasterListPage renders that PB form shape on the same
            generic /api/browse/zone CRUD endpoints (docs/design/zones-rebuild.md); the
            per-record edit page stays as the row drill-target below. */}
        <Route
          path="/config/zones"
          element={
            <RequireAuth>
              <ZoneMasterListPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/zones/:warehouseId/:zoneId"
          element={
            <RequireAuth>
              <ZonePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/domains"
          element={
            <RequireAuth>
              <DomainPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/states"
          element={
            <RequireAuth>
              <StatePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/states/:state"
          element={
            <RequireAuth>
              <StatePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/labor-codes"
          element={
            <RequireAuth>
              <LaborCodePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/labor-codes/:laborCode"
          element={
            <RequireAuth>
              <LaborCodePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/trailer-types"
          element={
            <RequireAuth>
              <TrailerTypePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/trailer-types/:trailerType"
          element={
            <RequireAuth>
              <TrailerTypePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/currencies"
          element={
            <RequireAuth>
              {/* slice-1237: PB w_currency main is an editable grid of ALL currencies
                  (dw_currency: Currency, Description, Conversion Factor), not a
                  single-record lookup. Render the 'currency' browse grid (genericCrud
                  add/save/delete; Change History drill -> w_currency_hi). Help finding
                  G-061: the unlinked /:uomId detail form (CurrencyPage) is retired — PB
                  w_currency is a single grid window with no per-currency form. */}
              <TableBrowserPage cfgKey="currency" />
            </RequireAuth>
          }
        />
        <Route
          path="/config/price-change-global"
          element={
            <RequireAuth>
              <PriceChangeGlobalPage />
            </RequireAuth>
          }
        />
        <Route
          path="/interfaces/inv-balance"
          element={
            <RequireAuth>
              <InvBalancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/inventory/move-pallet"
          element={
            <RequireAuth>
              <MovePalletPage />
            </RequireAuth>
          }
        />
        <Route
          path="/inventory/move-pieces-fr-location"
          element={
            <RequireAuth>
              <MovePiecesFrLocationPage />
            </RequireAuth>
          }
        />
        <Route
          path="/inventory/move-pieces-fr-pallet"
          element={
            <RequireAuth>
              <MovePiecesFrPalletPage />
            </RequireAuth>
          }
        />
        <Route
          path="/inventory/move-pieces-fr-assign"
          element={
            <RequireAuth>
              <MovePiecesFrAssignPage />
            </RequireAuth>
          }
        />
        <Route
          path="/inventory/move-pieces-fr-carton"
          element={
            <RequireAuth>
              <MovePiecesFrCartonPage />
            </RequireAuth>
          }
        />
        <Route
          path="/movers/ws"
          element={
            <RequireAuth>
              <MoversWsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/movers/rf"
          element={
            <RequireAuth>
              <MoversRfPage />
            </RequireAuth>
          }
        />
        <Route
          path="/movers/rf/:moverId"
          element={
            <RequireAuth>
              <MoversRfPage />
            </RequireAuth>
          }
        />
        <Route
          path="/movers/rf/:moverId/rf-screen"
          element={
            <RequireAuth>
              <RfScreensCurrentPage />
            </RequireAuth>
          }
        />
        <Route
          path="/movers/rf/:moverId/rf-screen-history"
          element={
            <RequireAuth>
              <RfScreensHistoryPage />
            </RequireAuth>
          }
        />
        {/* slice-3431 (#2467): PB's History grid drills into the SAME w_rfdriver_screen window
            with isparm_option='History' (w_rfdriver_screen_hi.srw dw_1::ue_rfdriver +
            doubleclicked). The row keys ride in the query string. */}
        <Route
          path="/movers/rf/:moverId/rf-screen-history/view"
          element={
            <RequireAuth>
              <RfScreensHistoryViewPage />
            </RequireAuth>
          }
        />
        <Route
          path="/rewarehousing/fm11"
          element={
            <RequireAuth>
              <Fm11Page />
            </RequireAuth>
          }
        />
        <Route
          path="/rewarehousing/velocity"
          element={
            <RequireAuth>
              <GlobalVelocityUpdatePage />
            </RequireAuth>
          }
        />
        <Route
          path="/rewarehousing/location-assignment"
          element={
            <RequireAuth>
              <GlobalLocationAssignmentPage />
            </RequireAuth>
          }
        />
        <Route
          path="/rewarehousing/chold"
          element={
            <RequireAuth>
              <CholdPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/fix-item-locations"
          element={
            <RequireAuth>
              <FixItemLocationPage />
            </RequireAuth>
          }
        />
        <Route
          path="/work/grab-status"
          element={
            <RequireAuth>
              <WorkGrabStatusPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/paper-pick-assign/start"
          element={
            <RequireAuth>
              <BatchPaperPickStartPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/paper-pick-assign/finish"
          element={
            <RequireAuth>
              <BatchPaperPickFinishPage />
            </RequireAuth>
          }
        />
        {/* slice-3008 (#2096): PB w_cart_assign_start — the Pick Ticket Assignment Search
            toolbar's Start (w_cart_assign_search ue_cart_start opens it with no parms,
            srw:108-115). Was mispointed at the Batch Paper Pick start page (a different
            PB window, w_assign_batch_paper_pick_start). */}
        <Route
          path="/orders/cart-assign/start"
          element={
            <RequireAuth>
              <CartAssignStartPage />
            </RequireAuth>
          }
        />
        {/* slice-3111: PB w_cart_assign_finish — the Pick Ticket Assignment Search toolbar's
            Finish (w_cart_assign_search ue_cart_finish opens it with the current row's
            warehouse/id/assign_type packet, srw:118-138). Was mispointed at the Batch Paper
            Pick finish page (a different PB window, w_assign_batch_paper_pick_finish). */}
        <Route
          path="/orders/cart-assign/finish"
          element={
            <RequireAuth>
              <CartAssignFinishPage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/generate-putaway-sheets"
          element={
            <RequireAuth>
              <GenPutawaySheetsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/fast-track-putaway-request"
          element={
            <RequireAuth>
              <FastTrackPutawayPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/generate-load-sheets"
          element={
            <RequireAuth>
              <GenLoadSheetsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/restocking/generate-restock-sheets"
          element={
            <RequireAuth>
              <GenRestockSheetsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/production/reconcile"
          element={
            <RequireAuth>
              <ProductionReconcilePage />
            </RequireAuth>
          }
        />
        <Route
          path="/work/convert-manual-to-paper"
          element={
            <RequireAuth>
              <ConvertManualPage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/qc-quick-release"
          element={
            <RequireAuth>
              <QcQuickReleasePage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/qc-pal-ctn-release"
          element={
            <RequireAuth>
              <QcPalCtnReleasePage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/qc-more-inventory"
          element={
            <RequireAuth>
              <QcMoreInventoryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/returns-order"
          element={
            <RequireAuth>
              <ReturnsOrderPage />
            </RequireAuth>
          }
        />
        <Route
          path="/putaway-sort/by-item"
          element={
            <RequireAuth>
              <PaSortByItemPage />
            </RequireAuth>
          }
        />
        <Route
          path="/putaway-sort/by-zone"
          element={
            <RequireAuth>
              <PaSortByZonePage />
            </RequireAuth>
          }
        />
        <Route
          path="/putaway-sort/rcv-by-zone"
          element={
            <RequireAuth>
              <RcvPaSortByZonePage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/returns-pickbin"
          element={
            <RequireAuth>
              <ReturnsPickbinPage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/returns-tote"
          element={
            <RequireAuth>
              <ReturnsTotePage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/returns-product"
          element={
            <RequireAuth>
              <ReturnsProductPage />
            </RequireAuth>
          }
        />
        <Route
          path="/inventory/contents-maint"
          element={
            <RequireAuth>
              <ContentsMaintPage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/receive-product"
          element={
            <RequireAuth>
              <ReceiveProductPage />
            </RequireAuth>
          }
        />
        {/* slice-3008 (#2092/#2093): PB w_production_run INSERT mode — "Production Run
            (New)" (open boolean1='Y', w_production_run.srw:41-58). Opened by the
            appoint-prod-line board's New (running PB; the export's "w_kit_run (insert)"
            at w_appoint_prod_line.srw:336-344 is stale w_appoint_kit clone code).
            /new must precede /:warehouseId/:productionRun. */}
        <Route
          path="/production-runs/new"
          element={
            <RequireAuth>
              <ProductionRunCreatePage />
            </RequireAuth>
          }
        />
        {/* #849 (slice-1114): zoom-target pages. */}
        <Route
          path="/production-runs/:warehouseId/:productionRun"
          element={
            <RequireAuth>
              <ProductionRunPage />
            </RequireAuth>
          }
        />
        <Route
          path="/style/status/:ownerId/:styleId/:colorId"
          element={
            <RequireAuth>
              <StyleStatusPage />
            </RequireAuth>
          }
        />
        {/* Card 48 (Chris 2026-08-06): PB w_trailer's OTHER open branch, ibparm_new
            (w_trailer.srw:52-118). Reached from Inbound Truck Maintenance's Show Trailer when
            the trailer does not exist yet (w_truckinbound.srw:505-527). One segment, so it
            cannot be confused with the two-segment maintenance route below — but keep it FIRST
            regardless, same rule as /kit-runs/new. */}
        <Route
          path="/yard/trailer/new"
          element={
            <RequireAuth>
              <TrailerMaintenancePage mode="new" />
            </RequireAuth>
          }
        />
        <Route
          path="/yard/trailer/:warehouseId/:trailerId"
          element={
            <RequireAuth>
              <TrailerMaintenancePage />
            </RequireAuth>
          }
        />
        {/* trailers rebuild (slice-1148): the search front-end /browse/trailers lacked. Rows drill
            into the EXISTING TrailerMaintenance detail above (one trailer-edit path). The old
            /browse/trailers grid redirects here. */}
        <Route
          path="/yard/trailer-search"
          element={
            <RequireAuth>
              <TrailerSearchPage />
            </RequireAuth>
          }
        />
        <Route path="/browse/trailers" element={<Navigate to="/yard/trailer-search" replace />} />
        {/* slices 2673-2681: PB w_kit_run (insert), opened by w_kit_run_search's
            toolbar New (ue_insertrow). /new must precede /:warehouseId/:kitRun. */}
        <Route
          path="/kit-runs/new"
          element={
            <RequireAuth>
              <KitRunCreatePage />
            </RequireAuth>
          }
        />
        {/* #849 (slice-1115): kit-run + item-cost zoom-target pages. */}
        <Route
          path="/kit-runs/:warehouseId/:kitRun"
          element={
            <RequireAuth>
              <KitRunPage />
            </RequireAuth>
          }
        />
        <Route
          path="/item-costs/shipment/:warehouseId/:ownerId/:masterPo/:masterReleaseNum"
          element={
            <RequireAuth>
              <ItemCostByShipmentPage />
            </RequireAuth>
          }
        />
        {/* wave-5 (§4.22): PB w_costing_by_shipment — the real costing-by-shipment-search
            Zoom target (ADR-0021): cost sheet + proc toolbar + create-costing flow. */}
        <Route
          path="/shipment-costs/:warehouseId/:ownerId/:masterPo/:masterReleaseNum"
          element={
            <RequireAuth>
              <ShipmentCostPage />
            </RequireAuth>
          }
        />
        {/* #849 (slice-1117): groups-for-user + inbound-asn zoom-target pages. */}
        <Route
          path="/users/:usrId/groups"
          element={
            <RequireAuth>
              <GroupsForUserPage />
            </RequireAuth>
          }
        />
        {/* #2733: the Insert icon's target — PB ue_insertrow opens a blank
            w_inbound_asn (insert) sheet (w_inbound_asn.srw:489-498). */}
        <Route
          path="/inbound-asn/new"
          element={
            <RequireAuth>
              <InboundAsnDetailPage isNew />
            </RequireAuth>
          }
        />
        <Route
          path="/inbound-asn/:warehouseId/:asnId"
          element={
            <RequireAuth>
              <InboundAsnDetailPage />
            </RequireAuth>
          }
        />
        {/* #849 (slice-1120): pre-receipt line detail zoom-target page. */}
        <Route
          path="/receipts/pre-receipt-line/:ownerId/:po/:releaseNum"
          element={
            <RequireAuth>
              <PreReceiptLineDetailPage />
            </RequireAuth>
          }
        />
        {/* #849 (slice-1128): Automatic Restocking workbench (rebuild of w_auto_rstk). The old
            read-only /browse/auto-rstk grid redirects here. */}
        <Route
          path="/restock/auto-restock"
          element={
            <RequireAuth>
              <AutoRestockPage />
            </RequireAuth>
          }
        />
        <Route path="/browse/auto-rstk" element={<Navigate to="/restock/auto-restock" replace />} />
        {/* #849 (slice-1133): Truck Load Waving workbench (rebuild of w_truck_sequence). Phase 1 —
            orders grid + assigned-orders pane + Save Seq. The old /browse/truck-sequence grid
            redirects here. Phases 2-5 add truck/wave assign + New Truck/Wave + Submit Wave. */}
        <Route
          path="/orders/truck-load-waving"
          element={
            <RequireAuth>
              <TruckLoadWavingPage />
            </RequireAuth>
          }
        />
        <Route path="/browse/truck-sequence" element={<Navigate to="/orders/truck-load-waving" replace />} />
        <Route
          path="/admin/imports/data"
          element={
            <RequireAuth>
              <ImportDataPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/imports/minmax"
          element={
            <RequireAuth>
              <MinMaxImportPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/imports/inventory"
          element={
            <RequireAuth>
              <InventoryImportPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/bol"
          element={
            <RequireAuth>
              <BolReportPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/wave"
          element={
            <RequireAuth>
              <WaveReportPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/cyc"
          element={
            <RequireAuth>
              <CycReportPage />
            </RequireAuth>
          }
        />
        {/* Slice-420 (BIG): PB w_rep_cyc full workflow port. The /reports/cyc
            route above stays for the legacy summary-report subset; this is the
            real "Generate Cycle Counts" workflow page. */}
        <Route
          path="/cyc/generate"
          element={
            <RequireAuth>
              <GenerateCycleCountsPage />
            </RequireAuth>
          }
        />
        {/* Slice-427: w_cyc_master maintenance workflow. */}
        <Route
          path="/cyc/master"
          element={
            <RequireAuth>
              <CycMasterPage />
            </RequireAuth>
          }
        />
        {/* Slice-427: w_inv_back_to_stock data-entry workflow. */}
        <Route
          path="/inventory/back-to-stock"
          element={
            <RequireAuth>
              <InvBackToStockPage />
            </RequireAuth>
          }
        />
        {/* Slice-430: w_pallet_back_to_stock data-entry workflow (sister). */}
        <Route
          path="/inventory/pallet-back-to-stock"
          element={
            <RequireAuth>
              <PalletBackToStockPage />
            </RequireAuth>
          }
        />
        {/* Slice-455: PB w_receipt_maint INSERT mode -- New Receipt create form. */}
        <Route
          path="/receipts/new"
          element={
            <RequireAuth>
              <ReceiptCreatePage />
            </RequireAuth>
          }
        />
        {/* Slice-800: /items/new is retired. PB INSERT mode now lives on ItemMasterPage
            at /items/master (no params). Redirect cached links so toolbar drill-ins and
            bookmarks keep working without a 404. */}
        <Route path="/items/new" element={<Navigate to="/items/master" replace />} />
        {/* Slice-457: PB w_customer_master INSERT mode -- New Customer create form. */}
        <Route
          path="/customer-master/new"
          element={
            <RequireAuth>
              <CustomerCreatePage />
            </RequireAuth>
          }
        />
        {/* slice-1614 (#1302/#1303): PB w_customer_shipto '(new)' mode -- New Customer
            Ship To create form (w_customer_shipto_search ue_insertrow). */}
        <Route
          path="/customer-shipto/new"
          element={
            <RequireAuth>
              <CustomerShipToCreatePage />
            </RequireAuth>
          }
        />
        {/* slice-1629 (#1446): PB w_customer_shipto '(retrieve)' mode -- the
            customer-shipto-search row action "Customer Ship To Maintenance" (ue_zoom,
            .srw:39-58) opens the SAME window/form as New, loaded with the selected
            row's owner_id/cust_id. Same component as /customer-shipto/new. */}
        <Route
          path="/customer-shiptos/:ownerId/:custId"
          element={
            <RequireAuth>
              <CustomerShipToCreatePage />
            </RequireAuth>
          }
        />
        {/* Slice-458: PB w_vendor INSERT mode -- New Vendor create form. */}
        <Route
          path="/vendor/new"
          element={
            <RequireAuth>
              <VendorCreatePage />
            </RequireAuth>
          }
        />
        {/* Slice-459: PB w_location INSERT mode -- New Location create form. */}
        <Route
          path="/location/new"
          element={
            <RequireAuth>
              <LocationCreatePage />
            </RequireAuth>
          }
        />
        {/* Slice-460: PB w_carrier INSERT mode -- New Carrier create form. */}
        <Route
          path="/carrier/new"
          element={
            <RequireAuth>
              <CarrierCreatePage />
            </RequireAuth>
          }
        />
        {/* Slice-461: PB w_ship_method INSERT mode -- New Ship Method create form. */}
        <Route
          path="/ship-method/new"
          element={
            <RequireAuth>
              <ShipMethodCreatePage />
            </RequireAuth>
          }
        />
        {/* Slice-462: PB w_owner INSERT mode -- New Owner create form. */}
        <Route
          path="/owner/new"
          element={
            <RequireAuth>
              <OwnerCreatePage />
            </RequireAuth>
          }
        />
        {/* slice-832: PB w_owner EDIT mode -- 9-tab maintenance of one owner. */}
        <Route
          path="/owners/:ownerId"
          element={
            <RequireAuth>
              <OwnerMaintenancePage />
            </RequireAuth>
          }
        />
        {/* Slice-463: PB w_warehouse INSERT mode -- New Warehouse create form. */}
        <Route
          path="/warehouse/new"
          element={
            <RequireAuth>
              <WarehouseCreatePage />
            </RequireAuth>
          }
        />
        {/* slice-831: PB w_warehouse EDIT mode -- 12-tab maintenance of one warehouse. */}
        <Route
          path="/warehouses/:warehouseId"
          element={
            <RequireAuth>
              <WarehouseMaintenancePage />
            </RequireAuth>
          }
        />
        {/* Slice-464: 6 reason_* INSERT-mode pages share one route w/ :type param. */}
        <Route
          path="/reason-code/:type/new"
          element={
            <RequireAuth>
              <ReasonCodeCreatePage />
            </RequireAuth>
          }
        />
        {/* Slice-465: 10 simple-config INSERT-mode pages share one route w/ :type param. */}
        <Route
          path="/simple-config/:type/new"
          element={
            <RequireAuth>
              <SimpleConfigCreatePage />
            </RequireAuth>
          }
        />
        {/* slice-2978 (Chris, 2026-07-20): the item-class medium-config entry was RETIRED
            by slice-1741 (#1525 — PB "New Item Class" is w_item_class_detail in NEW mode,
            not a flat 3-field dialog) but the stale URL still rendered the retired dialog,
            and the queue pool backfill served it to testers. Redirect to the faithful form. */}
        <Route
          path="/medium-config/item-class/new"
          element={<Navigate to="/config/item-class-detail/new" replace />}
        />
        {/* Slice-466: 8 medium-config INSERT-mode pages share one route w/ :type param. */}
        <Route
          path="/medium-config/:type/new"
          element={
            <RequireAuth>
              <MediumConfigCreatePage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/prod-in-out"
          element={
            <RequireAuth>
              <ProductionInOutReportPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/productivity-workbench"
          element={
            <RequireAuth>
              <ProductivityWorkbenchPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/billing-workbench"
          element={
            <RequireAuth>
              <BillingWorkbenchPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/dashboard"
          element={
            <RequireAuth>
              <DashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/web-report"
          element={
            <RequireAuth>
              <WebReportPage />
            </RequireAuth>
          }
        />
        {/* #43/#44 (slice-1144): RePrint PackStation Labels rebuild of w_label_print_history —
            query-first search page. Old /browse/label-print-history redirects here. */}
        <Route
          path="/labels/reprint"
          element={
            <RequireAuth>
              <ReprintLabelPage />
            </RequireAuth>
          }
        />
        <Route path="/browse/label-print-history" element={<Navigate to="/labels/reprint" replace />} />
        <Route
          path="/labels/central"
          element={
            <RequireAuth>
              <LabelCentralPage />
            </RequireAuth>
          }
        />
        <Route
          path="/labels/print-std"
          element={
            <RequireAuth>
              <LabelPrintStdPage />
            </RequireAuth>
          }
        />
        <Route
          path="/labels/va-item-labeling"
          element={
            <RequireAuth>
              <VaItemLabelingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/labels/va-item-bulk-labeling"
          element={
            <RequireAuth>
              <VaItemBulkLabelingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/labels/kit-building-serial"
          element={
            <RequireAuth>
              <KitBuildingSerialPage />
            </RequireAuth>
          }
        />
        <Route
          path="/labels/kit-building-multi-ser-lot"
          element={
            <RequireAuth>
              <KitBuildingMultiSerLotPage />
            </RequireAuth>
          }
        />
        <Route
          path="/inventory/global-location-maint"
          element={
            <RequireAuth>
              <GlobalLocationMaintPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/archive-ancient"
          element={
            <RequireAuth>
              <ArchiveAncientPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/apparel-itembuild"
          element={
            <RequireAuth>
              <ApparelItemBuildPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/quick-label-load"
          element={
            <RequireAuth>
              <QuickLabelLoadPage />
            </RequireAuth>
          }
        />
        <Route
          path="/activities/mat-cut-fast-pick"
          element={
            <RequireAuth>
              <MatCutFastPickPage />
            </RequireAuth>
          }
        />
        <Route
          path="/activities/mat-cut-remain-fast-pick"
          element={
            <RequireAuth>
              <MatCutRemainFastPickPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/quickship"
          element={
            <RequireAuth>
              <QuickShipPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/quick-pick"
          element={
            <RequireAuth>
              <QuickPickPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/purge-owner"
          element={
            <RequireAuth>
              <PurgeOwnerPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/purge"
          element={
            <RequireAuth>
              <PurgePage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/print-log"
          element={
            <RequireAuth>
              <PrintLogPage />
            </RequireAuth>
          }
        />
        <Route
          path="/production/big-board"
          element={
            <RequireAuth>
              <ProductionBigBoardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/status-dashboard"
          element={
            <RequireAuth>
              <OrderStatusDashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/restock/manual-stock-list"
          element={
            <RequireAuth>
              <ManualStockListBrowsePage />
            </RequireAuth>
          }
        />
        <Route
          path="/restock/manual-stock-list/:warehouseId/:ownerId/:listId"
          element={
            <RequireAuth>
              <ManualStockListDetailPage />
            </RequireAuth>
          }
        />
        <Route
          path="/material/cut"
          element={
            <RequireAuth>
              <MaterialCutPage />
            </RequireAuth>
          }
        />
        <Route
          path="/billing/manual"
          element={
            <RequireAuth>
              <ManualBillingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/vendor-qc/update"
          element={
            <RequireAuth>
              <VendorQcUpdatePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/clone-defaults"
          element={
            <RequireAuth>
              <CloneDefaultPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/clone-defaults/:tableName"
          element={
            <RequireAuth>
              <CloneDefaultPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/putwall-batches"
          element={
            <RequireAuth>
              <PutwallBatchPage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/line-search"
          element={
            <RequireAuth>
              <ReceiptLineSearchPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/conversions"
          element={
            <RequireAuth>
              <ConversionPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/conversions/:uomId"
          element={
            <RequireAuth>
              <ConversionPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/brokers"
          element={
            <RequireAuth>
              {/* slice-1238: PB w_broker main is an editable grid of ALL brokers
                  (dw_broker), not a single-record lookup. Render the 'broker' browse
                  grid. I-134: the unlinked single-broker form (/:brokerId) is retired. */}
              <TableBrowserPage cfgKey="broker" />
            </RequireAuth>
          }
        />
        <Route
          path="/config/countries"
          element={
            <RequireAuth>
              <CountryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/countries/:coo"
          element={
            <RequireAuth>
              <CountryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/consolidators"
          element={
            <RequireAuth>
              {/* slice-1239: PB w_consolidator shows ALL consolidators (dw_consolidator),
                  not a single-record lookup. slice-3806 (#3191): dw_consolidator is a
                  FREEFORM (processing=0), so the 'consolidator' cfg renders as the
                  all-records form (formViewAlways) — no grid, as in PB. ConsolidatorPage
                  stays as the /:consolId detail. */}
              <TableBrowserPage cfgKey="consolidator" />
            </RequireAuth>
          }
        />
        <Route
          path="/config/consolidators/:consolId"
          element={
            <RequireAuth>
              <ConsolidatorPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/divisions"
          element={
            <RequireAuth>
              <DivisionPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/divisions/:warehouseId/:ownerId/:divisionId"
          element={
            <RequireAuth>
              <DivisionPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/third-parties"
          element={
            <RequireAuth>
              <ThirdPartyPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/third-parties/:thirdpartyId"
          element={
            <RequireAuth>
              <ThirdPartyPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/bill-codes"
          element={
            <RequireAuth>
              <BillCodePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/bill-codes/:billCode"
          element={
            <RequireAuth>
              <BillCodePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/gl-codes"
          element={
            <RequireAuth>
              <GlCodePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/gl-codes/:glCode"
          element={
            <RequireAuth>
              <GlCodePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/nesting-codes"
          element={
            <RequireAuth>
              <NestingCodePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/nesting-codes/:nestingCode"
          element={
            <RequireAuth>
              <NestingCodePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/sales-reps"
          element={
            <RequireAuth>
              {/* slice-1238: PB w_sales_rep main is an editable grid of ALL salesreps
                  (dw_salesrep: Salesman, Last Name, First Name), not a single-record
                  lookup. Render the 'sales-rep' browse grid. SalesRepPage stays as the
                  /:salesId detail. */}
              <TableBrowserPage cfgKey="sales-rep" />
            </RequireAuth>
          }
        />
        <Route
          path="/config/sales-reps/:salesId"
          element={
            <RequireAuth>
              <SalesRepPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/invoice-companies"
          element={
            <RequireAuth>
              <InvoiceCompanyPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/invoice-companies/:companyId"
          element={
            <RequireAuth>
              <InvoiceCompanyPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/combine-categories"
          element={
            <RequireAuth>
              <CombineCategoryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/combine-categories/:ownerId/:combineCategory"
          element={
            <RequireAuth>
              <CombineCategoryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/order-types"
          element={
            <RequireAuth>
              {/* slice-1235: PB w_order_type main is an editable grid of ALL order
                  types (dw_order_type, ~20 cols), not a single-record lookup.
                  Render the 'order-type' browse grid (genericCrud add/save/delete).
                  slice-3151: the /:orderType orphan detail editor is retired — the
                  route below redirects here (see its comment). */}
              <TableBrowserPage cfgKey="order-type" />
            </RequireAuth>
          }
        />
        {/* slice-3151 (S-12 adjudication #2b, slice-3148 :taskId precedent): the
            /:orderType detail editor was an ORPHAN — reachable only by direct URL (no
            nav entry, the browse doesn't drill) and it bypassed every PB protect +
            wf_itemchanged cascade (bare form.register on pickRule/autowaveFlag/waveType/
            bomFlag/... with zero flag gating) that the /config/order-types sheet +
            MultiRecordFormView already port (w_order_type.srw:25-83). PB w_order_type is
            ONE w_dwtsheet_dbl — there is no detail window. Route stays registered so old
            links + hub reports don't 404 (route-drift rule); it redirects to the sheet. */}
        <Route
          path="/config/order-types/:orderType"
          element={
            <RequireAuth>
              <Navigate to="/config/order-types" replace />
            </RequireAuth>
          }
        />
        <Route
          path="/config/task-codes"
          element={
            <RequireAuth>
              <TaskCodePage />
            </RequireAuth>
          }
        />
        {/* slice-3148 (#2361 + Chris directive 07-28): the detail-editor paradigm was
            invented — PB w_task_codes is ONE in-grid editable sheet (w_dwtsheet, no
            zoom/detail window). The :taskId route stays registered so old links + hub
            reports don't 404 (route-drift rule); TaskCodePage redirects it to the sheet. */}
        <Route
          path="/config/task-codes/:taskId"
          element={
            <RequireAuth>
              <TaskCodePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/receipt-types"
          element={
            <RequireAuth>
              <ReceiptTypePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/receipt-types/:receiptType"
          element={
            <RequireAuth>
              <ReceiptTypePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/calc-server"
          element={
            <RequireAuth>
              <CalcServerPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/calc-server/:serverName"
          element={
            <RequireAuth>
              <CalcServerPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/receipt-default"
          element={
            <RequireAuth>
              <ReceiptDefaultPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/receipt-default/:defaultId"
          element={
            <RequireAuth>
              <ReceiptDefaultPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/order-default"
          element={
            <RequireAuth>
              <OrderDefaultPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/order-default/:defaultId"
          element={
            <RequireAuth>
              <OrderDefaultPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/cyc-categories"
          element={
            <RequireAuth>
              <CycCategoryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/cyc-categories/:category"
          element={
            <RequireAuth>
              <CycCategoryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/alarm-types"
          element={
            <RequireAuth>
              <AlarmTypePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/alarm-types/:alarmType"
          element={
            <RequireAuth>
              <AlarmTypePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/rtn-grps"
          element={
            <RequireAuth>
              <RtnGrpPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/rtn-grps/:rtnGrpId"
          element={
            <RequireAuth>
              <RtnGrpPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/work-types"
          element={
            <RequireAuth>
              <WorkTypePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/work-types/:workType"
          element={
            <RequireAuth>
              <WorkTypePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/va-rules"
          element={
            <RequireAuth>
              {/* slice-1236: PB w_va_rules main is an editable grid of ALL VA rules
                  (dw_va_rules: VA Rule, Description, Piece Process Time (m), Prio),
                  not a single-record lookup. Render the 'va-rules' browse grid
                  (genericCrud add/save/delete; Zoom→va-steps, New VA Rule drills).
                  VaRulePage stays as the /:vaRule detail. */}
              <TableBrowserPage cfgKey="va-rules" />
            </RequireAuth>
          }
        />
        <Route
          path="/config/va-rules/:vaRule"
          element={
            <RequireAuth>
              <VaRulePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/email-groups"
          element={
            <RequireAuth>
              <EmailGroupPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/email-groups/:emailDistCode"
          element={
            <RequireAuth>
              <EmailGroupPage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/notify-sets"
          element={
            <RequireAuth>
              {/* slice-1234: PB w_notification main is a grid of ALL notify sets
                  (dw_notification: Notification + Description), not a single-record
                  lookup. Render the 'notification' browse grid (genericCrud
                  add/save/delete inline; Zoom drills to the
                  w_notification_instruction child — PB's only nav action,
                  #1742 removed the duplicate Instructions icon). NotifySetPage stays as the
                  /:notifySet detail editor. */}
              <TableBrowserPage cfgKey="notification" />
            </RequireAuth>
          }
        />
        <Route
          path="/config/notify-sets/:notifySet"
          element={
            <RequireAuth>
              <NotifySetPage />
            </RequireAuth>
          }
        />
        <Route
          path="/putaway-errors"
          element={
            <RequireAuth>
              <PutawayErrorsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/truck-picklocsum"
          element={
            <RequireAuth>
              <TruckPickLocSumPage />
            </RequireAuth>
          }
        />
        <Route
          path="/receipts/pre-receipt-new"
          element={
            <RequireAuth>
              <NewPreReceiptPage />
            </RequireAuth>
          }
        />
        {/* slice-3647: Quick-Adjust is retired — both paths redirect onto the real
            lock-gated w_contents_maint port. See RetiredQuickAdjustRedirect above. */}
        <Route path="/inventory/adjust" element={<RetiredQuickAdjustRedirect />} />
        <Route
          path="/inventory/adjust/:warehouseId/:contentId"
          element={<RetiredQuickAdjustRedirect />}
        />
        <Route
          path="/inventory/inquiry"
          element={
            <RequireAuth>
              <InventoryInquiryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/inventory/inquiry/:itemId"
          element={
            <RequireAuth>
              <InventoryInquiryPage />
            </RequireAuth>
          }
        />
        {/* slice-881 (FB #15/#16): w_carton_pickpack_ship rebuilt as a dedicated
            scan-and-process station. Static path outranks /browse/:key, shadowing
            the old force-fit browse grid without a route/catalog change. */}
        <Route
          path="/browse/carton-pickpack-ship"
          element={
            <RequireAuth>
              <CartonPickpackShipPage />
            </RequireAuth>
          }
        />
        {/* slice-1028 (#669): w_ship_shipping is an activity window — dedicated page
            (processing-field header + dw_ship_select grid) shadows the bare browse. */}
        <Route
          path="/browse/ship-shipping"
          element={
            <RequireAuth>
              <ShipShippingPage />
            </RequireAuth>
          }
        />
        {/* flag-slice (al) #692/#863: w_ship_dayend is an operation window (station header
            + tag grid + Day End over tagged carriers + Zoom→ship-manifest). Shadow the bare
            /browse grid with the dedicated page, same as ship-shipping. */}
        <Route
          path="/browse/ship-dayend"
          element={
            <RequireAuth>
              <ShipDayEndPage />
            </RequireAuth>
          }
        />
        {/* slice-886 (FB #29/#30): w_carton_pickverify_ship station rebuild. */}
        <Route
          path="/browse/carton-pickverify-ship"
          element={
            <RequireAuth>
              <CartonPickverifyShipPage />
            </RequireAuth>
          }
        />
        {/* slice-3013 (§4.23, Chris platform ruling 2026-07-21): w_carton_repack_1_m is
            opened from TWO m_main.srm menu leaves with a mode parameter —
            m_multi_cartonrepack (string1='N', "Do not allow to pick") and
            m_multicartonpickandpack (string1='Y', "allow to pick"); the open event maps
            string1 → pick_flag + verbatim wf_title (.srw L584-590). ONE component, one
            route per leaf, mode LOCKED at launch — never a tab strip or mode picker.
            The legacy folded route redirects (old ?mode= links + bookmarks). */}
        <Route
          path="/browse/multi-carton-repack"
          element={
            <RequireAuth>
              <CartonRepack1MPage mode="repack" />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/multi-carton-pick-and-pack"
          element={
            <RequireAuth>
              <CartonRepack1MPage mode="pickpack" />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/carton-repack-1-m"
          element={
            <RequireAuth>
              <LegacyRepack1MRedirect />
            </RequireAuth>
          }
        />
        {/* slice-888 (FB #34/#35/#36, #39/#40 + 2 picked-consolidation issues):
            four more station rebuilds shadowing /browse/:key. */}
        <Route
          path="/browse/verify"
          element={
            <RequireAuth>
              <CartonVerifyPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/verify-w-ship"
          element={
            <RequireAuth>
              <CartonVerifyWShipPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/picked-consolidation"
          element={
            <RequireAuth>
              <PickedConsolidationPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/multi-src-pickpack-ship"
          element={
            <RequireAuth>
              <MultiSrcPickpackShipPage />
            </RequireAuth>
          }
        />
        {/* slice-907: final four carton-station rebuilds shadowing /browse/:key. */}
        <Route
          path="/browse/carton-pickpack-by-store"
          element={
            <RequireAuth>
              <CartonPickpackByStorePage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/carton-confirm"
          element={
            <RequireAuth>
              <CartonConfirmPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/carton-repack-ship-sio"
          element={
            <RequireAuth>
              <CartonRepackShipSioPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/asncartonpack"
          element={
            <RequireAuth>
              <AsnCartonPackPage />
            </RequireAuth>
          }
        />
        {/* slice-910: order-fastpick family station rebuilds shadowing /browse/:key. */}
        <Route
          path="/browse/order-fastpick"
          element={
            <RequireAuth>
              <OrderFastpickPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/order-fastpick-ship"
          element={
            <RequireAuth>
              <OrderFastpickShipPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/order-fastpick-build-1-m"
          element={
            <RequireAuth>
              <OrderFastpickBuild1MPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/order-fastpicktruck"
          element={
            <RequireAuth>
              <OrderFastPickTruckPage />
            </RequireAuth>
          }
        />
        {/* slice-911: kit-building family station rebuilds shadowing /browse/:key. */}
        <Route
          path="/browse/kit-building"
          element={
            <RequireAuth>
              <KitBuildingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/kit-building-batch-single-serial-bom"
          element={
            <RequireAuth>
              <KitBuildingBatchBomPage />
            </RequireAuth>
          }
        />
        {/* slice-923: truck carton load/unload station rebuilds shadowing /browse/:key. */}
        <Route
          path="/browse/truck-load-carton-to"
          element={
            <RequireAuth>
              <TruckLoadCartonToPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/truck-unload-carton-from"
          element={
            <RequireAuth>
              <TruckUnloadCartonFromPage />
            </RequireAuth>
          }
        />
        {/* slice-924: carton LTL consolidate/deconsolidate station rebuilds. */}
        <Route
          path="/browse/carton-move"
          element={
            <RequireAuth>
              <CartonMovePage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/carton-move-by-ord"
          element={
            <RequireAuth>
              <CartonMoveByOrdPage />
            </RequireAuth>
          }
        />
        {/* #481 (tester ANDREW): PB w_crt_language has 2 tabs (Work Station + Base).
            Static path shadows /browse/:key so the tabbed page is served instead. */}
        <Route
          path="/browse/crt-language"
          element={
            <RequireAuth>
              <TabbedCrtLanguagePage />
            </RequireAuth>
          }
        />
        {/* #483 (tester ANDREW): PB w_phfc_translate_menu has 2 tabs (System Menu + Window Menu)
            with a shared Language dropdown. Static path shadows /browse/:key. */}
        <Route
          path="/browse/cfg-menu-translate"
          element={
            <RequireAuth>
              <TabbedMenuTranslatePage />
            </RequireAuth>
          }
        />
        {/* #535 (tester ANDREW): PB w_pick_doc_form_lbl Zoom child has 2 tabs (Label
            Document form + Report Document form) keyed by pick_doc_form_id. Static path is
            declared before /browse/:key (the two tab grids are /browse/pick-doc-form-lbl
            and /browse/pick-doc-form-rep). */}
        <Route
          path="/config/pick-doc-form/:pickDocFormId"
          element={
            <RequireAuth>
              <TabbedPickDocFormPage />
            </RequireAuth>
          }
        />
        {/* #1532/#1543/#1542/#1544: PB w_work_mode_type "Work Types for Work Mode" — the Zoom
            child of Work Modes ({@code /browse/work-mode-type?workModePrefix=…}). Assignment
            matrix (all work types + Assigned checkbox); shadows the generic /browse/:key. */}
        <Route
          path="/browse/work-mode-type"
          element={
            <RequireAuth>
              <WorkModeTypePage />
            </RequireAuth>
          }
        />
        {/* slice-3138 (Chris GO 7-28 #3): PB w_import_sub_po_lines — ONE import workbench
            (all 7 registered icons: Save Profile / Delete Profile / Header / Reset Status /
            Import / Check Error / Process). Shadows the generic /browse/:key cfg. Design:
            docs/design/import-sub-po-lines-rebuild.md. */}
        <Route
          path="/browse/import-sub-po-lines"
          element={
            <RequireAuth>
              <ImportSubPoLinesPage />
            </RequireAuth>
          }
        />
        {/* slice-3017 (#2131 #1267): PB w_ph_obj_document "Documents for VA Rule: X" — the
            shared Documents drill target ({@code /browse/ph-obj-document?phObjType=…&objKey=…},
            legacy *Prefix params honored). Per-row Browse = REAL file upload; File Name click =
            document download (PB Zoom). Shadows the generic /browse/:key. */}
        <Route
          path="/browse/ph-obj-document"
          element={
            <RequireAuth>
              <PhObjDocumentPage />
            </RequireAuth>
          }
        />
        {/* #1608: PB w_combine_category_detail "Item Class Combine Category Detail" — the Zoom
            child of Combine Categories
            ({@code /browse/combine-category-detail?ownerId=…&combineCategory=…}). Membership
            matrix (every item_class + an "Allow" checkbox); shadows the generic /browse/:key. */}
        <Route
          path="/browse/combine-category-detail"
          element={
            <RequireAuth>
              <CombineCategoryDetailPage />
            </RequireAuth>
          }
        />
        {/* Tester issue #17 (w_qc_check / QC Inspect): the legacy /browse/qc-check
            generic config surfaced a flat list of unrelated dbxx.contents rows — no
            container-ID entry field, no contents-scoped-to-container, no qc_step list.
            PB w_qc_check is a scan-driven workflow (entry field → contents for that
            pallet/carton → the qc_rule's steps underneath); that structure does NOT fit
            the single-grid generic-browse shape, so it was rebuilt as the dedicated
            QcInspectPage at /qc/inspect (slice-819). nav.ts already routes the menu item
            there; this redirect catches the stale /browse/qc-check the tester bookmarked.
            Declared before /browse/:key so it shadows the leftover generic config. */}
        <Route path="/browse/qc-check" element={<Navigate to="/qc/inspect" replace />} />
        {/* slice-1060 (CHRIS): these screens were promoted /browse -> /config form-views
            (slices 949/950) / a tabbed page (slice-821 reasons), but the leftover generic
            /browse route still rendered a duplicate grid (and lingered as a second Testers
            Hub row). Redirect old -> canonical, same as /browse/qc-check. Declared before
            /browse/:key so they shadow the generic config. */}
        <Route path="/browse/calc-server" element={<Navigate to="/config/calc-server" replace />} />
        <Route path="/browse/order-default" element={<Navigate to="/config/order-default" replace />} />
        <Route path="/browse/receipt-default" element={<Navigate to="/config/receipt-default" replace />} />
        <Route path="/browse/cfg-reason-adjusted" element={<Navigate to="/config/reasons" replace />} />
        {/* flag-(ah) (#1036): retired orphan "Putaway Steps" browse (pb w_putprio). Nothing drills
            here since the rebuild — cfg-pa-rule Zoom opens the PB-exact /config/rules/putaway/:rule
            editor. Redirect to the parent putaway-rules browse (precedent: prod-labor-tasks). */}
        <Route path="/browse/cfg-pa-steps" element={<Navigate to="/browse/cfg-pa-rule" replace />} />
        {/* slice-2983 (default-visibility-58): retired orphan "Pick Steps" browse (pb w_pickprio),
            same flag-(ah) class — cfg-pick-rule Zoom opens the PB-exact /config/rules/pick/:rule
            editor; the orphan grid hid 28 PB-visible columns. Redirect to the parent browse. */}
        <Route path="/browse/cfg-pick-steps" element={<Navigate to="/browse/cfg-pick-rule" replace />} />
        {/* tester #653: PB w_pick_ticket_assign ("Simple Order Assignment") single-order
            FORM. Declared before /browse/:key so it shadows the generic browse. */}
        <Route
          path="/browse/pick-ticket-assign"
          element={
            <RequireAuth>
              <SimpleOrderAssignPage />
            </RequireAuth>
          }
        />
        {/* slice-1079: third workstation batch shadowing the generic /browse/:key. */}
        <Route path="/browse/value-add-pick" element={<RequireAuth><ValueAddPickPage /></RequireAuth>} />
        <Route path="/browse/merge-order-pallet" element={<RequireAuth><MergePalletPage /></RequireAuth>} />
        {/* slice-1078: second workstation batch shadowing the generic /browse/:key. */}
        <Route path="/browse/trucks" element={<RequireAuth><TruckDispatchPage /></RequireAuth>} />
        {/* slice-3918 (#3690): PB w_truck_load (LOAD / UNLOAD), one route per mode. */}
        <Route path="/browse/truck-load" element={<RequireAuth><TruckLoadPage mode="LOAD" /></RequireAuth>} />
        <Route path="/browse/truck-unload" element={<RequireAuth><TruckLoadPage mode="UNLOAD" /></RequireAuth>} />
        {/* slice-3259: PB w_truck_pop_import — the Truck Search Import toolbar item
            (w_truck_search.srw:986-991) opens it as a w_response. Its own route rather than a
            modal: it is a full 29-column import workbench, and the other import screens are
            routed pages too. */}
        <Route path="/truck/import" element={<RequireAuth><TruckImportPage /></RequireAuth>} />
        <Route path="/browse/configuration" element={<RequireAuth><ConfigurationPage /></RequireAuth>} />
        {/* slice-3020 (#2126, §4.23): w_configuration's SECOND PB menu leaf — Window >
            User Setting (m_main.srm:7455-7456, gstr_winparm.string1='(usr)') — its own
            mode-locked route; /browse/configuration stays the (sys) leaf (m_sysconfig,
            m_main.srm:38043-38044). No rename → no ROUTE_ALIASES entry needed. */}
        <Route path="/browse/configuration-user" element={<RequireAuth><ConfigurationPage mode="usr" /></RequireAuth>} />
        <Route path="/browse/inventory-search" element={<RequireAuth><InventorySearchPage /></RequireAuth>} />
        <Route path="/browse/value-add" element={<RequireAuth><ValueAddProcessPage /></RequireAuth>} />
        {/* slice-1077: dedicated workstation pages shadowing the generic /browse/:key. */}
        <Route path="/browse/work-adjustment" element={<RequireAuth><WorkAdjustmentPage /></RequireAuth>} />
        <Route path="/browse/dock-manager-door" element={<RequireAuth><DockManagerPage /></RequireAuth>} />
        <Route path="/browse/profile-header" element={<RequireAuth><ReportLauncherPage /></RequireAuth>} />
        <Route path="/browse/purge-tran" element={<RequireAuth><PurgeTranPage /></RequireAuth>} />
        {/* tester #1485: w_clear_ctr — warning header + non-grid rebuild from the PB source. */}
        <Route path="/browse/clear-ctr" element={<RequireAuth><ResetCounterPage /></RequireAuth>} />
        {/* w_wave_fastpick_pallbuild — committing pack-station, dedicated page (decision #19). */}
        <Route
          path="/activities/wave-fastpick-pallbuild"
          element={
            <RequireAuth>
              <WaveFastpickPallbuildPage />
            </RequireAuth>
          }
        />
        <Route
          path="/browse/:key"
          element={
            <RequireAuth>
              <TableBrowserPage />
            </RequireAuth>
          }
        />
        <Route
          path="/pb/:window"
          element={
            <RequireAuth>
              <PbPlaceholderPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/pb/rpt-wave-estimate"
          element={
            <RequireAuth>
              <ShipmentAnalysisByWavePage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/pb/:reportKey"
          element={
            <RequireAuth>
              <PbReportRunnerPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/ask-me"
          element={
            <RequireAuth>
              <AskMePage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/builder"
          element={
            <RequireAuth>
              <ReportBuilderPage />
            </RequireAuth>
          }
        />
        <Route
          path="/profile/password"
          element={
            <RequireAuth>
              <ChangePasswordPage />
            </RequireAuth>
          }
        />
        <Route
          path="/profile/productivity"
          element={
            <RequireAuth>
              <ProductivitySwitcherPage />
            </RequireAuth>
          }
        />
        <Route
          path="/vendor/maintenance"
          element={
            <RequireAuth>
              <VendorMaintenancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/vendor/maintenance/:vendorId"
          element={
            <RequireAuth>
              <VendorMaintenancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/location/maintenance"
          element={
            <RequireAuth>
              <LocationMaintenancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/location/maintenance/:locationId"
          element={
            <RequireAuth>
              <LocationMaintenancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/location/status"
          element={
            <RequireAuth>
              <LocationStatusPage />
            </RequireAuth>
          }
        />
        <Route
          path="/location/status/:warehouseId/:locationId"
          element={
            <RequireAuth>
              <LocationStatusPage />
            </RequireAuth>
          }
        />
        <Route
          path="/customer-master/maintenance"
          element={
            <RequireAuth>
              <CustomerMasterMaintenancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/customer-master/maintenance/:custCode"
          element={
            <RequireAuth>
              <CustomerMasterMaintenancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireAuth>
              <ReportsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/feedback/queue"
          element={
            <RequireAuth>
              <FeedbackQueuePage />
            </RequireAuth>
          }
        />
        <Route
          path="/config/security"
          element={
            <RequireAuth>
              <SecurityConfigPage />
            </RequireAuth>
          }
        />
        <Route
          path="/cartons/activity-launcher"
          element={
            <RequireAuth>
              <CartonActivityLauncherPage />
            </RequireAuth>
          }
        />
        <Route
          path="/cartons/doc-reprint"
          element={
            <RequireAuth>
              <CartonDocReprintPage />
            </RequireAuth>
          }
        />
        <Route
          path="/cartons/merge-ship"
          element={
            <RequireAuth>
              <CartonMergeShipPage />
            </RequireAuth>
          }
        />
        <Route
          path="/cartons/overpack-ship"
          element={
            <RequireAuth>
              <CartonOverpackShipPage />
            </RequireAuth>
          }
        />
        <Route
          path="/cartons/borderfree-ship"
          element={
            <RequireAuth>
              <CartonBorderfreeShipPage />
            </RequireAuth>
          }
        />
        <Route
          path="/cartons/recombine-ship"
          element={
            <RequireAuth>
              <CartonRecombineShipPage />
            </RequireAuth>
          }
        />
        <Route
          path="/cartons/put-to-store"
          element={
            <RequireAuth>
              <PutToStorePage />
            </RequireAuth>
          }
        />
        <Route
          path="/cartons/process"
          element={
            <RequireAuth>
              <CartonProcessPage />
            </RequireAuth>
          }
        />
        <Route
          path="/cartons/induction"
          element={
            <RequireAuth>
              <CartonInductionPage />
            </RequireAuth>
          }
        />
        {/* slice-1152 (Chris) + slice-3012 (#2069, dev-rules §4.23): w_carton_repack backs
            3 PB menu leaves via gstr_winparm.string1 (m_main.srm — "w_carton_repack
            (repack)" / "(pick pack)" / "(pick pack check)"). One component, one route per
            menu leaf, mode LOCKED at launch (the ?mode= override is gone) — never a tab
            strip or runtime mode picker. Each route pins its PB-verbatim wf_title. */}
        <Route
          path="/cartons/repack"
          element={
            <RequireAuth>
              <CartonRepackPage mode="REPACK" />
            </RequireAuth>
          }
        />
        <Route
          path="/cartons/pickpack"
          element={
            <RequireAuth>
              <CartonRepackPage mode="COP" />
            </RequireAuth>
          }
        />
        <Route
          path="/cartons/pickpack-check"
          element={
            <RequireAuth>
              <CartonRepackPage mode="COP_CHECK" />
            </RequireAuth>
          }
        />
        <Route
          path="/cartons/pickpack-ship-sio"
          element={
            <RequireAuth>
              <CartonPickpackShipSioPage />
            </RequireAuth>
          }
        />
        {/* #12/#13/#75 (slice-1143): Carton Lookup rebuild of w_cartlook — dedicated scan/tabs
            page. The old /browse/carton-lookup grid redirects here. */}
        <Route
          path="/cartons/lookup"
          element={
            <RequireAuth>
              <CartonLookupPage />
            </RequireAuth>
          }
        />
        {/* wave-5 (§4.22): query-preserving redirect so the zoom-prefill deep links
            (?carton / ?workSeqNum / ?orderId&releaseNum) survive the hop to /cartons/lookup. */}
        <Route path="/browse/carton-lookup" element={<CartonLookupRedirect />} />
        <Route
          path="/ship/void"
          element={
            <RequireAuth>
              <ShipVoidPage />
            </RequireAuth>
          }
        />
        <Route
          path="/ship/rateshop"
          element={
            <RequireAuth>
              <RateShopOrderPage />
            </RequireAuth>
          }
        />
        <Route
          path="/ship/misc"
          element={
            <RequireAuth>
              <ShipMiscPage />
            </RequireAuth>
          }
        />
        <Route
          path="/ship/batch"
          element={
            <RequireAuth>
              <ShipBatchPage />
            </RequireAuth>
          }
        />
        <Route
          path="/ship/by-carton"
          element={
            <RequireAuth>
              <ShipByCartonPage />
            </RequireAuth>
          }
        />
        <Route
          path="/ship/multi-cartons"
          element={
            <RequireAuth>
              <ShipMultiCartonsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/ship/dayend"
          element={
            <RequireAuth>
              <ShipDayEndOpenPage />
            </RequireAuth>
          }
        />
        <Route
          path="/ship/reprocess-error"
          element={
            <RequireAuth>
              <ShipReprocessErrorPage />
            </RequireAuth>
          }
        />
        <Route
          path="/ship/station"
          element={
            <RequireAuth>
              <ShipStationPage />
            </RequireAuth>
          }
        />
        {/* Slice-778: Testers Hub. Meta-tooling page (no PageLayout/pbWindow)
            so the sign-off + report-issue toolbar doesn't render on the hub
            itself -- signing the hub off is meaningless. */}
        {/* slice-2849: the full hub is admin-only (chris/link/link1); everyone else is
            redirected to the guided Test Screens queue. */}
        <Route
          path="/testers-hub"
          element={
            <RequireAuth>
              <TestScreensAdminGate>
                <TestersHubPage />
              </TestScreensAdminGate>
            </RequireAuth>
          }
        />
        <Route
          path="/testers-hub/test-card"
          element={
            <RequireAuth>
              <TestCardPopoutPage />
            </RequireAuth>
          }
        />
        <Route
          path="/testers-hub/functional"
          element={
            <RequireAuth>
              <FunctionalRoundPage />
            </RequireAuth>
          }
        />
        <Route
          path="/test-screens"
          element={
            <RequireAuth>
              <TestScreensPage />
            </RequireAuth>
          }
        />
        {/* slice-3683 (CHRIS): the functional round's guided queue. */}
        <Route
          path="/functional-test-screens"
          element={
            <RequireAuth>
              <FunctionalTestScreensPage />
            </RequireAuth>
          }
        />
        {/* slice-3930: TEMPORARY "My top screens" survey (404s server-side when the flag is off). */}
        <Route
          path="/my-top-screens"
          element={
            <RequireAuth>
              <MyTopScreensPage />
            </RequireAuth>
          }
        />
        {/* #952 (CHRIS): bulk PB-screenshot capture page (multi-user claim/lease). */}
        <Route
          path="/load-screenshots"
          element={
            <RequireAuth>
              <LoadScreenshotsPage />
            </RequireAuth>
          }
        />
        {/* slice-1608 (CHRIS): USB scale + Zebra ZPL hardware test rig (ADR-0002 vehicle). */}
        <Route
          path="/hardware-test"
          element={
            <RequireAuth>
              <HardwareTestPage />
            </RequireAuth>
          }
        />
        {/* slice-1607 (CHRIS): PB-vs-PH10 report PDF capture + verification status. */}
        <Route
          path="/report-pdf-capture"
          element={
            <RequireAuth>
              <ReportPdfCapturePage />
            </RequireAuth>
          }
        />
        <Route
          path="/report-pdf-status"
          element={
            <RequireAuth>
              <ReportPdfStatusPage />
            </RequireAuth>
          }
        />
        {/* slice-918 (CHRIS): rolling activity log, in-app under its nav item. */}
        <Route
          path="/whats-happening"
          element={
            <RequireAuth>
              <WhatsHappeningPage />
            </RequireAuth>
          }
        />
        {/* slice-3549 (CHRIS): v2 experiment campaign — the /v2-screens index (nav, below
            Testers Hub) plus the hidden v2 routes aggregated from app/v2/. The PB-faithful
            pages are untouched; see app/v2/README.md for the contract. */}
        <Route
          path="/v2-screens"
          element={
            <RequireAuth>
              <V2ScreensPage />
            </RequireAuth>
          }
        />
        {v2Routes}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </NewScreensSwap>
      </RequireWindow>
    </Suspense>
  );
}

function NotFound() {
  return <div className="text-slate-500">Not found.</div>;
}
